import { GENERIC_CREATION_RETIRED, isComposedPipeline } from '@djgg/creations';
/**
 * AssetStudio — the asset flow for one creation. The LIBRARY (attached assets)
 * is home: the resting view a creator lands on, each asset a card with its art,
 * name, a kind + provenance badge, and obvious Edit / Detach controls. Making or
 * changing an asset happens in a 3-step COMPOSER wizard:
 *   1·What   — pick a kind + name (new), or a locked "editing the X" header (edit).
 *   2·Source — Generate / Curated / Upload, the sources a kind can come from
 *              (`KIND_SOURCES` — data, not branches). Generate is a prompt for
 *              visuals or a closed BRIEF for music/SFX (→ `generateAssetsFn`,
 *              the builder's async job); Curated is the platform's bank, bound
 *              per cue through the one bindings surface; Upload is
 *              bring-your-own PNGs / WAVs (→ `uploadAssetsFn`, no model call,
 *              no quota). Generated audio is gated by the platform flags and
 *              the first live build (ADR 0009 Q5), with the reason shown on
 *              the disabled action and the provider training-use disclosure
 *              beside it (ADR 0009 Q1 / row 1.11).
 *   3·Choose — the candidate grid → approve/attach. Auto-advances here when the
 *              target's candidates arrive; attaching returns to the library.
 *              Audio jobs yield ONE candidate each; earlier candidates stay
 *              selectable and another is an explicit "Generate another".
 * Editing an existing asset opens the composer pre-filled under the SAME name —
 * attaching then REPLACES that asset in place (edit-in-place). Detach
 * (`detachAssetFn`) is the release valve for the builder's asset-bindings gate
 * (every attached asset must be used by every build). Members act directly; a
 * superadmin non-member is routed through the attribution modal (same contract
 * as every other mutation). Generation is capped per member per day
 * (`maxAssetGensPerDay`) — the quota error is surfaced plainly.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { pinnedPlayUrl } from '../services/playUrl.js';
import {
  isCodeOnlyPipeline,  ANIM_FRAME_DEFAULTS,
  ANIM_STATE_NAMES,
  audioBriefRefusalDetail,
  composeCoverPrompt,
  isAnimStateName,
  isValidAssetName,
  redrawableAssets,
  slugify,
  stripAssetName,
  validateMusicBriefV1,
  validateSfxBriefV1,
  type AnimStateName,
  type AssetGenJob,
  type Creation,
  type CreationAsset,
  type CreationAssetKind,
  type CreationAssetSource,
  type CreationVersion,
  type PresentationEditV1,
} from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { useSettings } from '../hooks/useSettings.js';
import { useSubscription } from '../hooks/useSubscription.js';
import type { AssetVariant } from '../services/types.js';
import { Mascot } from '../components/Mascot.js';
import { JobProcessExpander } from './JobProcessDetail.js';
import { StripPreview } from './StripPreview.js';
import { rolesByAsset, visualUsage } from './visualUsage.js';
import { assetPublicationLabel, assetPublicationState } from './assetLifecycle.js';
import { MediaReviewPanel } from './MediaReviewPanel.js';
import { SfxBindingsPanel } from './SfxBindingsPanel.js';
import { AudioUploadPanel } from './AudioUploadPanel.js';
import { PresentationPanel } from './PresentationPanel.js';
import { AssetDetails } from './AssetDetails.js';
import { AudioBriefFields } from './AudioBriefForm.js';
import { AvoidDirectionField } from './AvoidDirectionField.js';
import {
  AUDIO_GENERATION_DISCLOSURE,
  AUDIO_KIND_LABEL,
  audioGenerateBlocker,
  briefFormFromBrief,
  briefFormIsWizardPrefill,
  briefFormPrefill,
  briefFormProblem,
  briefFromForm,
  isAudioKind,
  type AudioBriefForm,
} from './audioBrief.js';
import { useAssetDecision } from './useAssetDecision.js';
import { createAssetRequestRecovery } from './assetRequestRecovery.js';
import { AssetLibraryNavigation, assetMatchesLibraryFilter, type AssetLibraryFilter } from './AssetLibraryNavigation.js';
import { MAX_VISUAL_UPLOAD_BYTES as MAX_UPLOAD_BYTES, MAX_VISUAL_UPLOAD_FILES as MAX_UPLOAD_FILES, isPngUpload, type VisualUploadKind, visualFileToBase64 as fileToBase64 } from './visualUpload.js';

/** Creator-authored kinds — the UPLOAD surface offers exactly these (strips
 *  are platform-generated only; `uploadAssets` rejects kind 'strip'). */
const ASSET_KINDS = ['sprite', 'background', 'icon', 'palette', 'cover'] as const;

/** Composer-selectable kinds: the creator kinds + 'strip' (generate-only) +
 *  the two audio kinds (members only — audio requires creation membership). */
const COMPOSER_KINDS = [...ASSET_KINDS, 'strip', 'music', 'sfx'] as const;
type ComposerKind = (typeof COMPOSER_KINDS)[number];
type CoverSource = 'game-assets' | 'concept';

/** The three SOURCES an asset can come from. */
type ComposerSource = 'generate' | 'curated' | 'upload';
const SOURCE_LABEL: Record<ComposerSource, string> = { generate: 'Generate', curated: 'Curated', upload: 'Upload' };
/** Which sources each kind offers — the ONE table the source tabs render from.
 *  Strips are platform-generated (the server rejects a strip upload). Visual
 *  kinds have no creator-pickable curated catalog: curated placeholders are
 *  applied by builds, so the tab is not offered rather than shown dead. Audio
 *  offers all three; its Curated source is the per-cue bindings surface. */
const KIND_SOURCES: Record<ComposerKind, readonly ComposerSource[]> = {
  sprite: ['generate', 'upload'],
  background: ['generate', 'upload'],
  icon: ['generate', 'upload'],
  palette: ['generate', 'upload'],
  cover: ['generate', 'upload'],
  strip: ['generate'],
  music: ['generate', 'curated', 'upload'],
  sfx: ['generate', 'curated', 'upload'],
};

/** Picker label per kind (kinds read as-is except the animation strip). */
const KIND_LABEL: Partial<Record<ComposerKind, string>> = { strip: 'Animation strip', music: 'Music', sfx: 'Sound effect' };

const PUBLISH_KIND_LABEL: Readonly<Record<string, string>> = {
  sprite: 'Sprite',
  background: 'Background',
  strip: 'Animation',
};

/** One-line explainer per asset kind (shown next to the kind picker). */
const KIND_HELP: Record<ComposerKind, string> = {
  sprite: 'A single game object with a see-through background — a player, enemy, bullet or pickup.',
  icon: 'A small UI mark — a life heart, a coin, a button glyph.',
  background: 'The full playfield behind the action, sized to the arena.',
  palette: 'A set of colors the game draws with, instead of a picture.',
  cover: 'The wide 16:9 key art — your gallery card and the in-game title screen.',
  strip:
    'A short animation for one entity, drawn from its attached sprite — pick the entity and the motion; it applies on the next build.',
  music:
    'Background music for play — a loop of about 90 seconds. Generate it from a brief, bind no music, or upload a WAV; then bind it and publish saved media.',
  sfx:
    'A short sound for one game cue. Generate it from a description, bind the curated bank, or upload a WAV; then bind it to a cue and publish saved media.',
};

/** Human label for a running job's coarse stage. */
const STAGE_LABEL: Record<string, string> = {
  generating: 'Generating…',
  postprocessing: 'Cleaning up art…',
  inspecting: 'Checking the audio…',
  uploading: 'Uploading variants…',
};

/** Provenance badge per {@link CreationAssetSource}: where this asset came from,
 *  in plain words. Unstamped (pre-provenance) assets get no badge. */
const SOURCE_BADGE: Partial<Record<CreationAssetSource, string>> = {
  studio: 'AI',
  bank: 'From build',
  build: 'From build',
  upload: 'Uploaded',
};

/** The reserved name for a creation's cover / menu-splash asset — attaching a
 *  `cover` under this name drives the gallery card + in-game splash. */
const COVER_NAME = 'cover';


/** Composer view + step machine. `library` is home; `new`/`edit` run the wizard. */
type View = 'library' | 'new' | 'edit';
type Step = 1 | 2 | 3;

/** A picked-but-not-yet-uploaded PNG: the file + an editable name/kind + a
 *  local preview URL (revoked when the row is removed or the batch uploads). */
interface UploadRow {
  id: string;
  file: File;
  objectUrl: string;
  name: string;
  kind: VisualUploadKind;
}

/** Callable errors → creator-readable messages (quota is the common one). */
function friendlyError(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : '';
  if (/resource.exhausted|limit reached/i.test(msg)) return msg.replace(/^.*?:\s*/, '');
  return msg || fallback;
}

/** A live generic game's art is republished only once it is rebuilt with the composed builder. */
export const GENERIC_ART_NEEDS_REBUILD = 'This game was made with the previous builder. To change its art, first rebuild it with the new builder in Iterate; then its art can be changed and republished here.';

export function AssetStudio({
  creation,
  mediaReviewPilot = false,
}: {
  creation: Creation & { id: string };
  mediaReviewPilot?: boolean;
}) {
  const services = useServices();
  const { user } = useAuth();
  const mediaPilot = mediaReviewPilot && user?.role === 'superadmin';
  const audioAccess = !!user && (creation.creatorUid === user.uid || !!creation.memberUids?.includes(user.uid));
  const [reviewTarget, setReviewTarget] = useState<{ creationId: string; variantId: string; audioOnly?: boolean } | null>(null);
  const [mediaRefresh, setMediaRefresh] = useState(0);
  function invalidateMediaChoices() {
    // Trigger both authoritative readers, not just cache eviction: the policy
    // subscription may already have arrived before the callable resolves.
    setMediaRefresh(value => value + 1);
    setAttachedIds(new Set());
  }
  const { runAttributed, attributionModal } = useAttributedAction();
  // The published version is the useful reference here: Assets answers "what
  // do players see?", not "what did the most recent draft happen to render?"
  const visualVersion = creation.activeVersion || creation.latestVersion;
  const liveVersion = useSubscription<(CreationVersion & { id: string }) | null>(
    (cb) => (visualVersion > 0 ? services.subscribe.version(creation.id, visualVersion, cb) : () => {}),
    null,
    [services, creation.id, visualVersion],
  );
  /** Bound assets selected for one deliberate visual-refresh iterate. Selection
   * lives on the asset card itself; there is no duplicate preview picker. */
  const [selectedVisualAssets, setSelectedVisualAssets] = useState<string[]>([]);
  // The library/composer machine. `view` is the whole surface; `step` +
  // `sourceTab` drive the wizard once a composer is open.
  const [view, setView] = useState<View>('library');
  const [searchParams] = useSearchParams();
  const requestedMedia = searchParams.get('media');
  const [libraryFilter, setLibraryFilter] = useState<AssetLibraryFilter>(() => {
    return requestedMedia === 'music' || requestedMedia === 'sound-effects' ? requestedMedia : 'all';
  });
  useEffect(() => {
    setLibraryFilter(requestedMedia === 'music' || requestedMedia === 'sound-effects' ? requestedMedia : 'all');
  }, [requestedMedia]);
  const [step, setStep] = useState<Step>(1);
  const [sourceTab, setSourceTab] = useState<ComposerSource>('generate');
  // Prefill with the stored cheap-LLM example asset prompt (an asset that would
  // suit this game) as an editable starting point; ✨/Clear restore or empty it.
  const [prompt, setPrompt] = useState(() => creation.suggestedAssetPrompt ?? '');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ComposerKind>('sprite');
  // The audio brief (music/sfx Generate source) — prefilled from the wizard's
  // direction (row 1.7) whenever the composer opens on an audio kind.
  const [audioBrief, setAudioBrief] = useState<AudioBriefForm>(() => briefFormPrefill('music', creation.gameDesign?.audio));
  // Provider flags (ADR 0009): both ship OFF; the Generate action for audio is
  // visibly disabled with the reason until a superadmin turns a kind on.
  const { settings } = useSettings();
  /** Pick a kind; the source tab follows the kind's table (a strip has only
   *  Generate, so an Upload tab cannot linger from a previous kind). */
  const chooseKind = (next: ComposerKind) => {
    setKind(next);
    if (!KIND_SOURCES[next].includes(sourceTab)) setSourceTab(KIND_SOURCES[next][0]);
    if (isAudioKind(next)) setAudioBrief(briefFormPrefill(next, creation.gameDesign?.audio));
  };
  // Covers should resemble the game by default. A creator can deliberately
  // choose concept art when that is the direction they want instead.
  const [coverSource, setCoverSource] = useState<CoverSource>('game-assets');
  // Strip composer target (kind 'strip' only): the design entity + the state.
  const [stripEntity, setStripEntity] = useState('');
  const [stripState, setStripState] = useState<AnimStateName>('move');
  // Async generation jobs for this creation (live) + the variants each SUCCEEDED
  // job produced (fetched once on completion). Subscription-fed + fetch-on-mount
  // so a generation SURVIVES navigating away / reloading / switching assets.
  const [jobs, setJobs] = useState<Array<AssetGenJob & { id: string }>>([]);
  const [variantsByJob, setVariantsByJob] = useState<Record<string, AssetVariant[]>>({});
  const mediaReadEpoch = useRef(0);
  const { requestDecision, decisionPanel } = useAssetDecision(JSON.stringify([
    creation.id, creation.assetPolicyRevision ?? 0, mediaRefresh,
    (creation.assets ?? []).map(asset => [asset.name, asset.variantId, asset.url, asset.contentSha256]),
    Object.entries(variantsByJob).map(([id, variants]) => [id, variants.map(variant => [variant.id, variant.reviewState])]),
  ]));
  const [starting, setStarting] = useState(false);
  const generating = useRef(false);
  const generationRecovery = useRef<ReturnType<typeof createAssetRequestRecovery> | null>(null);
  if (!generationRecovery.current) {
    let storage: Storage | null = null;
    try { storage = window.localStorage; } catch { /* Private browsing may deny storage. */ }
    generationRecovery.current = createAssetRequestRecovery(storage);
  }
  // Set true on Generate; when the target's variants arrive we auto-advance to
  // step 3 (Choose) and clear it — so a manual step-back never yanks forward.
  const [awaitingVariants, setAwaitingVariants] = useState(false);
  const [attachingId, setAttachingId] = useState<string | null>(null);
  const [attachedIds, setAttachedIds] = useState<Set<string>>(new Set());
  const [detachingName, setDetachingName] = useState<string | null>(null);
  const [detachConfirmation, setDetachConfirmation] = useState<{ creationId: string; asset: CreationAsset } | null>(null);
  const detachInFlight = useRef(false);
  const detachConfirmationRef = useRef<HTMLElement>(null);
  const currentCreation = useRef(creation);
  currentCreation.current = creation;
  useEffect(() => {
    if (detachConfirmation?.creationId === creation.id) detachConfirmationRef.current?.focus();
  }, [detachConfirmation, creation.id]);
  useEffect(() => {
    if (detachConfirmation && (detachConfirmation.creationId !== creation.id ||
      !creation.assets?.some(asset => asset.name === detachConfirmation.asset.name &&
        asset.variantId === detachConfirmation.asset.variantId && asset.url === detachConfirmation.asset.url &&
        asset.contentSha256 === detachConfirmation.asset.contentSha256))) setDetachConfirmation(null);
  }, [creation.id, creation.assets, detachConfirmation]);
  const [publishingAssets, setPublishingAssets] = useState(false);
  const publishingRef = useRef(false);
  const [publishConfirmation, setPublishConfirmation] = useState<{ creationId: string; presentationEdit?: PresentationEditV1 } | null>(null);
  const publishConfirmationRef = useRef<HTMLElement>(null);
  const [publishPreview, setPublishPreview] = useState<{ creationId: string; notPublished: Array<{ variantId: string; name: string; kind: string }> } | null>(null);
  const [publishPreviewError, setPublishPreviewError] = useState('');
  useEffect(() => {
    if (publishConfirmation?.creationId === creation.id) publishConfirmationRef.current?.focus();
  }, [publishConfirmation, creation.id]);
  const currentCreationId = useRef(creation.id);
  currentCreationId.current = creation.id;
  const [publishError, setPublishError] = useState('');
  const [error, setError] = useState('');
  // Jobs whose variants we've already fetched (avoid refetch on every emit).
  const fetchedJobs = useRef<Set<string>>(new Set());
  // 1s ticker: drives the elapsed-time readout while a job is outstanding.
  const [, setNowTick] = useState(0);
  // Bring-your-own-art: rows staged from the file picker, uploaded + attached
  // in one step.
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const uploadInFlight = useRef(false);
  const [uploadApproval, setUploadApproval] = useState<string | null>(null);
  const uploadApprovalRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Library views belong to one creation. Never carry an empty/pilot-only
  // filter into the next game's authoritative asset inventory.
  useEffect(() => {
    setLibraryFilter(requestedMedia === 'music' || requestedMedia === 'sound-effects' ? requestedMedia : 'all');
    setPublishConfirmation(null);
    setPublishingAssets(false);
    setUploadApproval(null);
    setDetachConfirmation(null);
    clearUploads();
  }, [creation.id]);

  // Slug-shape the typed name live (same canonicalization the server enforces).
  const canonicalName = slugify(name);
  const nameValid = isValidAssetName(canonicalName);

  // A durable rejection/undo invalidates candidate decisions even if the
  // generation job itself did not change. Fence old requests, not just cache
  // entries, so a slower pre-rejection response cannot reopen a blocked choice.
  useEffect(() => {
    mediaReadEpoch.current += 1;
    fetchedJobs.current.clear();
    setVariantsByJob({});
  }, [services, creation.id, creation.assetPolicyRevision, mediaRefresh]);

  // Live async-generation jobs for this creation — the progress + recovery feed.
  // Fetch-on-mount (the subscription's seed emission) is what makes a generation
  // survive navigation: outstanding + recently-completed jobs are re-read
  // authoritatively rather than reconstructed from missed realtime events.
  useEffect(() => {
    let active = true;
    setJobs([]); // A different creation must not inherit the prior game's history.
    const unsub = services.subscribe.assetGenJobs(
      creation.id,
      (rows) => {
        if (!active) return;
        setJobs((previous) => {
          // A realtime re-fetch is a bounded window, not a creator decision.
          // Keep terminal rows already seen in this visit so an unreviewed
          // generated choice cannot disappear while the creator is deciding.
          const next = new Map(rows.map((job) => [job.id, job]));
          for (const job of previous) {
            if (!next.has(job.id) && (job.status === 'succeeded' || job.status === 'failed')) {
              next.set(job.id, job);
            }
          }
          return [...next.values()].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
        });
      },
      (e) => { if (active) setError(friendlyError(e, 'Could not load generation status.')); },
    );
    return () => { active = false; unsub(); };
  }, [services, creation.id]);

  // Review state is durable creator work, unlike the bounded realtime job
  // feed. Rehydrate every unresolved variant directly so an older successful
  // job cannot vanish just because it fell outside that activity window.
  useEffect(() => {
    const epoch = mediaReadEpoch.current;
    void services
      .fetchAssetVariants(creation.id, { reviewState: 'pending' })
      .then((variants) => {
        if (epoch !== mediaReadEpoch.current) return;
        const byJob = new Map<string, AssetVariant[]>();
        for (const variant of variants) {
          if (!variant.genJobId) continue;
          const list = byJob.get(variant.genJobId) ?? [];
          list.push(variant);
          byJob.set(variant.genJobId, list);
        }
        if (byJob.size === 0) return;
        setVariantsByJob((previous) => ({ ...previous, ...Object.fromEntries(byJob) }));
        setJobs((previous) => {
          const known = new Set(previous.map((job) => job.id));
          const recovered = [...byJob.entries()]
            .filter(([jobId]) => !known.has(jobId))
            .map(([jobId, jobVariants]) => ({
              id: jobId,
              creationId: creation.id,
              kind: jobVariants[0].kind as AssetGenJob['kind'],
              name: jobVariants[0].name,
              prompt: jobVariants[0].prompt,
              status: 'succeeded' as const,
              workerId: null,
              heartbeatAt: null,
              attempts: 0,
              createdBy: '',
              createdByHandle: '',
              actionPerformedBy: '',
              actionPerformedByHandle: '',
              createdAt: 0,
              finishedAt: null,
              error: null,
              stage: null,
              traceGcsUri: '',
              variantCount: jobVariants.length,
            }));
          return recovered.length > 0 ? [...previous, ...recovered] : previous;
        });
      })
      .catch(() => undefined);
  }, [services, creation.id, creation.assetPolicyRevision, mediaRefresh]);

  // For each SUCCEEDED job we haven't fetched yet, pull its variants once so
  // they're attachable (guard every rendered field — a realtime UPDATE replaces
  // the whole row). Runs on every jobs emit; the ref set makes it fetch-once.
  useEffect(() => {
    const epoch = mediaReadEpoch.current;
    for (const job of jobs) {
      if (job.status !== 'succeeded' || fetchedJobs.current.has(job.id)) continue;
      fetchedJobs.current.add(job.id);
      void services
        .fetchAssetVariants(creation.id, { genJobId: job.id })
        .then((vs) => {
          if (epoch !== mediaReadEpoch.current) return;
          // A succeeded job with ZERO rows is a read raced against the worker's
          // variant commit (the worker commits variants BEFORE flipping the job
          // status — the ordering invariant) — evict so a later emit retries
          // instead of caching the empty read forever.
          if (vs.length === 0) {
            fetchedJobs.current.delete(job.id);
            return;
          }
          setVariantsByJob((prev) => ({ ...prev, [job.id]: vs }));
        })
        .catch(() => {
          if (epoch === mediaReadEpoch.current) fetchedJobs.current.delete(job.id);
        }); // allow a later retry without invalidating a newer policy read
    }
  }, [jobs, services, creation.id, creation.assetPolicyRevision, mediaRefresh]);

  // Tick once a second only while something is in flight (elapsed readout).
  const anyOutstanding = jobs.some((j) => j.status === 'awaiting-core' || j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    if (!anyOutstanding) return;
    const t = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [anyOutstanding]);

  // Already-attached assets (server-owned; streams in via the creation prop).
  const attached = creation.assets ?? [];
  const attachedByName = new Map(attached.map((a) => [(a.name ?? '').toLowerCase(), a]));
  const liveOnlyAssets = creation.activeVersion && liveVersion?.n === creation.activeVersion
    ? (liveVersion.assetsSnapshot ?? []).filter(snapshot => !attached.some(asset =>
      asset.name === snapshot.name && asset.kind === snapshot.kind && asset.variantId === snapshot.variantId))
    : [];
  const selectedVisualAssetNames = selectedVisualAssets;
  const visualRefreshHref = `?tab=iterate&visuals=${encodeURIComponent(selectedVisualAssetNames.join(','))}`;
  const liveVisuals = visualUsage(liveVersion?.manifestSummary);
  const liveRolesByAsset = rolesByAsset(liveVisuals);
  // Art a BUILD can redraw: build-time generation derives its plan from the game
  // design, so a name the design does not plan can never be regenerated however
  // firmly the game binds it. Being bound is what used to qualify an asset for
  // "Refresh in Iterate", which is how a studio background outside the design
  // (bounty-bob's `deep-shaft-bg`) got selected for a redraw that was then
  // dropped in silence. `requestBuildFn` refuses such a selection outright, so
  // the affordance must not offer it — same `redrawableAssets`, one answer.
  const redrawableAssetNames = new Set(
    redrawableAssets(creation.gameDesign, attached).map((asset) => asset.name.toLowerCase()),
  );
  const connectedAssetNames = new Set(liveRolesByAsset.keys());
  const connectedAssets = attached.filter((asset) => connectedAssetNames.has(asset.name.toLowerCase()));
  const otherAssets = attached.filter((asset) => !connectedAssetNames.has(asset.name.toLowerCase()));
  const visibleConnectedAssets = connectedAssets.filter((asset) => assetMatchesLibraryFilter(libraryFilter, asset.kind));
  const visibleOtherAssets = otherAssets.filter((asset) => assetMatchesLibraryFilter(libraryFilter, asset.kind));
  const visibleAttachedAssets = attached.filter((asset) => assetMatchesLibraryFilter(libraryFilter, asset.kind));
  const pendingBuildAssets = attached.filter(
    (asset) =>
      (asset.kind === 'sprite' || asset.kind === 'background' || asset.kind === 'strip') &&
      !liveVersion?.assetsSnapshot?.some((snapshot) => snapshot.variantId === asset.variantId),
  );
  // Attach calls update local review state before the creation subscription
  // echoes the new asset list. Include those just-approved variants so the
  // next-build banner and CTA never disappear during that short round trip.
  const localDrawableVariantIds = new Set(
    Object.values(variantsByJob)
      .flat()
      .filter((variant) =>
        variant.kind === 'sprite' || variant.kind === 'background' || variant.kind === 'strip')
      .map((variant) => variant.id),
  );
  const locallyAttachedPendingAssets = Object.values(variantsByJob)
    .flat()
    .filter((variant) =>
      attachedIds.has(variant.id) &&
      localDrawableVariantIds.has(variant.id) &&
      !attached.some((asset) => asset.variantId === variant.id));
  const attachedBuildChanges = [
    ...pendingBuildAssets.map((asset) => ({
      variantId: asset.variantId,
      name: asset.name,
      kind: asset.kind,
    })),
    ...locallyAttachedPendingAssets.map((asset) => ({
      variantId: asset.id,
      name: asset.name,
      kind: asset.kind,
    })),
  ];
  // The server decides what a composed publish binds. The studio shows the rest
  // as not published, and offers no publish until it has that answer.
  const composedLive = isComposedPipeline(liveVersion?.pipelineSnapshot);
  const publishPreviewKey = composedLive && attachedBuildChanges.length > 0
    ? attachedBuildChanges.map((asset) => asset.variantId).join(',')
    : '';
  useEffect(() => {
    if (!publishPreviewKey) return;
    let stale = false;
    setPublishPreviewError('');
    services.callable.previewAssetPublish({ creationId: creation.id }).then((preview) => {
      if (!stale) setPublishPreview({ creationId: creation.id, notPublished: preview.notPublished });
    }).catch((err: unknown) => {
      if (!stale) setPublishPreviewError(friendlyError(err, 'Could not check which assets can be published.'));
    });
    return () => { stale = true; };
  }, [services, creation.id, publishPreviewKey]);
  const previewForCreation = publishPreview?.creationId === creation.id ? publishPreview : null;
  const notPublishedChanges = composedLive && publishPreviewKey ? previewForCreation?.notPublished ?? [] : [];
  const pendingBuildChanges = composedLive && !previewForCreation
    ? []
    : attachedBuildChanges.filter((asset) => !notPublishedChanges.some((left) => left.variantId === asset.variantId));
  const readyBuildChangeCount = pendingBuildChanges.length;

  // A live generic game's art changes after it is rebuilt with the composed builder (Iterate); a generic game
  // that never went live stays retired.
  const genericNotice = creation.activeVersion > 0 ? GENERIC_ART_NEEDS_REBUILD : GENERIC_CREATION_RETIRED;

  function requestAssetPublication() {
    if (!isComposedPipeline(liveVersion?.pipelineSnapshot)) { setPublishError(genericNotice); return; }
    if (publishingRef.current || creation.buildState === 'building') return;
    setPublishError('');
    setPublishConfirmation({ creationId: creation.id });
  }

  async function publishApprovedAssets() {
    if (!isComposedPipeline(liveVersion?.pipelineSnapshot)) { setPublishError(genericNotice); return; }
    const confirmation = publishConfirmation;
    if (publishingRef.current || creation.buildState === 'building' || confirmation?.creationId !== creation.id) return;
    const publishingCreationId = confirmation.creationId;
    publishingRef.current = true;
    setPublishError('');
    setPublishingAssets(true);
    try {
      await runAttributed(creation, async (attributeTo) => {
        await services.callable.requestAssetPublish({
          creationId: creation.id,
          commandId: crypto.randomUUID(),
          ...(confirmation.presentationEdit ? { presentationEdit: confirmation.presentationEdit } : {}),
          ...(attributeTo ? { attributeTo } : {}),
        });
      });
    } catch (err) {
      if (currentCreationId.current === publishingCreationId) {
        setPublishError(friendlyError(err, 'Could not publish the approved assets.'));
      }
    } finally {
      publishingRef.current = false;
      if (currentCreationId.current === publishingCreationId) {
        setPublishingAssets(false);
        setPublishConfirmation(null);
      }
    }
  }

  function toggleVisualAsset(assetName: string) {
    setSelectedVisualAssets((current) =>
      current.includes(assetName)
        ? current.filter((name) => name !== assetName)
        : [...current, assetName],
    );
  }

  // ── Strip target derivations (from the LIVE design prop — an iterate can
  // land mid-compose and change the entity roster under us) ──────────────────
  const designEntities = creation.gameDesign?.entities ?? [];
  /** True when the entity has an attached TAILORED base sprite (the attached
   *  `sprite` under the entity's slug — the SAME `slugify` the server's
   *  reference resolution uses; a bank placeholder doesn't count). */
  const hasBaseSprite = (entityName: string): boolean => {
    const base = attachedByName.get(slugify(entityName));
    return !!base && base.kind === 'sprite' && base.source !== 'bank';
  };
  /** The picked entity still exists in the live design (exact name). */
  const stripEntityLive = designEntities.some((e) => e.name === stripEntity);
  /** Derived `<entity>-<state>` name via the SHARED helper — client and server
   *  must agree on it (dedupe + pending predicates key on it). '' when no
   *  entity is picked or the composition can't form a valid slug. */
  const stripName = (() => {
    if (!stripEntity) return '';
    try {
      return stripAssetName(stripEntity, stripState);
    } catch {
      return '';
    }
  })();
  // The composer's effective target: derived for strips, typed for the rest.
  const targetName = kind === 'strip' ? stripName : canonicalName;
  const targetValid =
    kind === 'strip' ? !!stripName && stripEntityLive && hasBaseSprite(stripEntity) : nameValid;

  // Generation-job views: outstanding (progress), succeeded (variant grids),
  // failed (surfaced errors). An in-flight job for the CURRENT form target
  // blocks a duplicate Generate (the server dedupes too — this is the UI guard).
  const outstanding = jobs.filter((j) => j.status === 'awaiting-core' || j.status === 'queued' || j.status === 'running');
  const succeededJobs = jobs.filter((j) => j.status === 'succeeded');
  const failedJobs = jobs.filter((j) => j.status === 'failed');
  const outstandingForForm = outstanding.find((j) => j.name === targetName && j.kind === kind);
  const pendingFor = (n: string, k: string): boolean =>
    outstanding.some((j) => j.name === n && j.kind === k);
  const isVariantAttached = (variant: AssetVariant): boolean =>
    attachedIds.has(variant.id) ||
    attachedByName.get((variant.name ?? '').toLowerCase())?.variantId === variant.id;
  const isVariantReviewed = (variant: AssetVariant): boolean =>
    isVariantAttached(variant) ||
    variant.reviewState === 'skipped' ||
    variant.reviewState === 'rejected' ||
    variant.reviewState === 'superseded';
  // A normal image job offers alternatives, so one approved variant resolves
  // it. A strip job is a set of complementary motion states: it remains in the
  // review shelf until EVERY returned state is attached. This is intentionally
  // derived from server-owned attached assets after a reload, not a transient
  // “we showed this once” flag.
  const isJobConsumed = (job: AssetGenJob & { id: string }): boolean =>
    job.kind === 'strip'
      ? (variantsByJob[job.id]?.length ?? 0) > 0 && (variantsByJob[job.id] ?? []).every(isVariantReviewed)
      : (variantsByJob[job.id] ?? []).some(isVariantAttached) ||
        (variantsByJob[job.id]?.length ?? 0) > 0 && (variantsByJob[job.id] ?? []).every(isVariantReviewed);
  // Succeeded jobs whose variants (already fetched) target the CURRENT form
  // (name + kind) — the composer's step-3 grid, and the auto-advance trigger.
  const targetJobs = succeededJobs.filter(
    (j) => j.name === targetName && j.kind === kind && (variantsByJob[j.id]?.length ?? 0) > 0,
  );
  // Succeeded jobs whose variants are still un-attached — surfaced on the LIBRARY
  // as a "ready to review" shelf so a generation you walked away from (or one
  // that finished while you were elsewhere) isn't stranded inside a closed
  // composer. This is also the ONLY variant surface that survives a cold reload,
  // since the realtime feed doesn't replay missed events.
  const pendingReviewJobs = succeededJobs.filter(
    (j) => (variantsByJob[j.id]?.length ?? 0) > 0 && !isJobConsumed(j),
  );
  const pendingAnimationReviewJobs = pendingReviewJobs.filter((job) => job.kind === 'strip');
  const visiblePendingReviewJobs = pendingReviewJobs.filter((job) => assetMatchesLibraryFilter(libraryFilter, job.kind));

  // ── Missing-states surface: entities with DECLARED animations, chipped per
  // state. missing = declared − attached − outstanding − pendingReview: a
  // succeeded-but-unreviewed job counts as in-flight even before its variants
  // fetch, so a chip never flaps to "missing" during a completion burst. ──────
  const declaredAnimations = designEntities
    .map((e) => ({
      entityName: e.name,
      states: (e.sprite?.animations ?? []).map((a) => a.state).filter(isAnimStateName),
    }))
    .filter((e) => e.states.length > 0);
  const stripStatus = (entityName: string, state: AnimStateName): 'attached' | 'in-flight' | 'missing' => {
    let derived: string;
    try {
      derived = stripAssetName(entityName, state); // the SAME shared derivation
    } catch {
      return 'missing'; // unsluggable entity — a strip can never exist for it
    }
    if (attachedByName.get(derived)?.kind === 'strip') return 'attached';
    if (pendingFor(derived, 'strip')) return 'in-flight';
    if (succeededJobs.some((j) => j.name === derived && j.kind === 'strip' && !isJobConsumed(j))) {
      return 'in-flight'; // pending review (variant fetch may still be racing)
    }
    return 'missing';
  };

  // Auto-advance to Choose once the generation we're waiting on delivers.
  useEffect(() => {
    if (awaitingVariants && targetJobs.length > 0) {
      setStep(3);
      setAwaitingVariants(false);
    }
  }, [awaitingVariants, targetJobs.length]);

  /** Open the composer for a NEW asset of `startKind` (the library's audio
   *  tabs open straight onto their kind; everywhere else starts on a sprite). */
  function openNew(startKind: ComposerKind) {
    setView('new');
    setStep(1);
    setSourceTab('generate');
    setName('');
    setKind(startKind);
    setCoverSource('game-assets');
    setStripEntity('');
    setStripState('move');
    setPrompt(creation.suggestedAssetPrompt ?? '');
    if (isAudioKind(startKind)) setAudioBrief(briefFormPrefill(startKind, creation.gameDesign?.audio));
    setAwaitingVariants(false);
    setError('');
  }

  /** A one-click cover action: it uses the normal async asset pipeline, which
   * supplies the currently attached game art as generation references. */
  function openCoverComposer() {
    setView('new');
    setStep(2);
    setSourceTab('generate');
    setName(COVER_NAME);
    setKind('cover');
    setCoverSource('game-assets');
    setStripEntity('');
    setStripState('move');
    coverFromDesign();
    setAwaitingVariants(false);
    setError('');
  }

  /** Open the composer pre-filled for one entity's missing state — the
   *  "Generate" shortcut on the Animations chips. */
  function openStripComposer(entityName: string, state: AnimStateName) {
    setView('new');
    setStep(1);
    setSourceTab('generate');
    setName('');
    setKind('strip');
    setStripEntity(entityName);
    setStripState(state);
    setAwaitingVariants(false);
    setError('');
  }

  /** Open the composer pre-filled from an attached asset — the edit-in-place
   *  entry point: generate/upload fresh art under the SAME name, attach it, and
   *  it replaces the current asset. Name + kind are locked in this mode. */
  function openEdit(asset: CreationAsset) {
    // Strips are platform-generated (derived name + re-packed geometry) — no
    // edit-in-place composer for them; strip regeneration is its own flow.
    if (asset.kind === 'strip') return;
    setView('edit');
    setStep(1);
    setSourceTab('generate');
    setName(asset.name);
    setKind(asset.kind);
    setCoverSource('game-assets');
    setPrompt(asset.prompt);
    // An audio asset's stored prompt is the RENDERED brief, not the fields; the
    // replacement brief starts from the wizard direction like a new one.
    if (isAudioKind(asset.kind)) setAudioBrief(briefFormPrefill(asset.kind, creation.gameDesign?.audio));
    setAwaitingVariants(false);
    setError('');
  }

  function backToLibrary() {
    setView('library');
    setStep(1);
    setAwaitingVariants(false);
    clearUploads();
    setError('');
  }

  const savedGeneration = user ? generationRecovery.current.read(user.uid, creation.id) : null;
  const recoverableGeneration = (() => {
    if (!savedGeneration || typeof savedGeneration !== 'object') return null;
    const p = savedGeneration as Record<string, unknown>;
    if (p.creationId !== creation.id) return null;
    if (p.kind === 'strip') {
      const s = p.strip as { entityName?: unknown; state?: unknown } | undefined;
      return s && typeof s.entityName === 'string' && typeof s.state === 'string' && isAnimStateName(s.state)
        ? { kind: 'strip' as const, entityName: s.entityName, state: s.state } : null;
    }
    if (typeof p.name !== 'string') return null;
    if (p.kind === 'music' || p.kind === 'sfx') {
      // The saved brief is re-validated by the port's own validators: a stale
      // or hand-edited entry is dropped rather than restored half-shaped.
      try {
        return p.kind === 'music'
          ? { kind: 'music' as const, name: p.name, brief: briefFormFromBrief({ kind: 'music', brief: validateMusicBriefV1(p.brief) }) }
          : { kind: 'sfx' as const, name: p.name, brief: briefFormFromBrief({ kind: 'sfx', brief: validateSfxBriefV1(p.brief) }) };
      } catch {
        return null;
      }
    }
    if (typeof p.kind !== 'string' || !['sprite', 'background', 'icon', 'palette', 'cover'].includes(p.kind) ||
      typeof p.prompt !== 'string') return null;
    return { kind: p.kind as Exclude<CreationAssetKind, 'strip' | 'sfx' | 'music'>,
      name: p.name, prompt: p.prompt, coverSource: p.coverSource === 'concept' ? 'concept' as const : 'game-assets' as const };
  })();

  function restoreGeneration() {
    if (!recoverableGeneration || generating.current) return;
    setView('new'); setStep(2); setSourceTab('generate');
    setKind(recoverableGeneration.kind);
    if (recoverableGeneration.kind === 'strip') {
      setStripEntity(recoverableGeneration.entityName); setStripState(recoverableGeneration.state); setName('');
    } else if (recoverableGeneration.kind === 'music' || recoverableGeneration.kind === 'sfx') {
      setName(recoverableGeneration.name); setAudioBrief(recoverableGeneration.brief);
    } else {
      setName(recoverableGeneration.name); setPrompt(recoverableGeneration.prompt); setCoverSource(recoverableGeneration.coverSource);
    }
    setAwaitingVariants(false); setError('');
  }

  // ── Audio Generate gating + the one refusal surface ─────────────────────────
  // ADR 0009 Q5/Q6: the action is disabled with its reason (flag off, or no
  // live build yet), and a provider that DECLINED the brief is shown as a
  // refusal with the provider's reason — not filed under "couldn't produce".
  const audioKind = isAudioKind(kind) ? kind : null;
  const audioBlocker = audioKind ? audioGenerateBlocker(audioKind, settings.audioGeneration, creation.activeVersion) : null;
  // Every path that puts an audio kind in the composer (chooseKind, openNew,
  // openEdit, restoreGeneration) also sets the brief for that kind, so the two
  // never disagree.
  const audioBriefProblem = audioKind ? briefFormProblem(audioBrief, creation.avoidDirection?.audio) : null;
  const audioRefusal = audioKind
    ? failedJobs
        .filter((j) => j.name === targetName && j.kind === audioKind)
        .map((j) => audioBriefRefusalDetail(j.error))
        .find((detail): detail is string => detail !== null) ?? null
    : null;

  async function generate() {
    if (generating.current || !user) return;
    generating.current = true;
    setError('');
    setStarting(true);
    try {
      await runAttributed(creation, async (attributeTo) => {
        // Strip requests carry ONLY the target — the server derives the name,
        // composes the prompt, and resolves the reference base sprite.
        // Audio requests carry the closed BRIEF and no prompt — the server
        // composes the provider text, folding in the saved avoid line.
        const payload = {
          creationId: creation.id,
          ...(kind === 'strip'
            ? { kind, strip: { entityName: stripEntity, state: stripState } }
            : isAudioKind(kind)
              ? { kind, name: canonicalName, brief: briefFromForm(audioBrief).brief }
              : {
                  kind,
                  prompt: prompt.trim(),
                  name: canonicalName,
                  ...(kind === 'cover' ? { coverSource } : {}),
                }),
          ...(attributeTo ? { attributeTo } : {}),
        };
        const intent = generationRecovery.current!.prepare(user.uid, creation.id, payload);
        await services.callable.generateAssets({ ...payload, requestId: intent.requestId });
        intent.complete();
        // No variants yet — the queued job appears via the subscription and
        // auto-advances to the Choose step on completion.
        setAwaitingVariants(true);
      });
    } catch (err) {
      setError(friendlyError(err, 'Asset generation failed.'));
    } finally {
      generating.current = false;
      setStarting(false);
    }
  }

  async function attach(variant: AssetVariant, replaces: CreationAsset | undefined) {
    if (
      replaces &&
      !await requestDecision(
        `Replace attached ${replaces.kind} "${replaces.name}" with this image? ` +
          `Future builds will use the new art.`,
      )
    ) {
      return;
    }
    setError('');
    setAttachingId(variant.id);
    try {
      await runAttributed(creation, async (attributeTo) => {
        if (variant.kind === 'strip') {
          await services.callable.attachAnimationSet({
            creationId: creation.id,
            variantIds: [variant.id],
            ...(attributeTo ? { attributeTo } : {}),
          });
        } else {
          await services.callable.attachAsset({
            creationId: creation.id,
            variantId: variant.id,
            ...(attributeTo ? { attributeTo } : {}),
          });
        }
        setAttachedIds((prev) => new Set(prev).add(variant.id));
        setVariantsByJob((previous) =>
          Object.fromEntries(
            Object.entries(previous).map(([jobId, variants]) => [
              jobId,
              variants.map((candidate) =>
                candidate.id === variant.id ? { ...candidate, reviewState: 'attached' as const } : candidate,
              ),
            ]),
          ),
        );
        // Approve & attach completes the wizard — back to the library, where the
        // new/updated asset now shows as a card.
        backToLibrary();
      });
    } catch (err) {
      setError(friendlyError(err, 'Attach failed.'));
    } finally {
      setAttachingId(null);
    }
  }

  /** Review every unresolved state in one generated animation set. Attaching
   * is one atomic backend request; dismissals remain independent decisions but
   * share one confirmation and attribution choice. */
  async function reviewStripSet(
    job: AssetGenJob & { id: string },
    decision: 'attach' | 'skip',
  ) {
    const pending = (variantsByJob[job.id] ?? []).filter((variant) => !isVariantReviewed(variant));
    if (pending.length === 0) return;
    const replacements = pending.filter((variant) => {
      const existing = attachedByName.get((variant.name ?? '').toLowerCase());
      return existing && existing.variantId !== variant.id;
    }).length;
    const allHaveCurrent = replacements === pending.length;
    const confirmed = await requestDecision(
      decision === 'attach'
        ? `Use all ${pending.length} new animation states for "${job.name}"? ` +
            `${replacements > 0 ? `${replacements} current state${replacements === 1 ? '' : 's'} will be replaced. ` : ''}` +
            `The changes will apply on the next build.`
        : allHaveCurrent
          ? `Keep the current animations for "${job.name}"? ` +
              `All ${pending.length} generated state${pending.length === 1 ? '' : 's'} will be dismissed.`
          : `Skip all ${pending.length} generated animation states for "${job.name}"? ` +
              `The generated results will be dismissed.`,
    );
    if (!confirmed) return;

    setError('');
    setAttachingId(`bulk:${job.id}`);
    let completed = 0;
    try {
      await runAttributed(creation, async (attributeTo) => {
        if (decision === 'attach') {
          const variantIds = pending.map((variant) => variant.id);
          await services.callable.attachAnimationSet({
            creationId: creation.id,
            variantIds,
            ...(attributeTo ? { attributeTo } : {}),
          });
          completed = pending.length;
          setAttachedIds((previous) => {
            const next = new Set(previous);
            for (const variantId of variantIds) next.add(variantId);
            return next;
          });
          setVariantsByJob((previous) => ({
            ...previous,
            [job.id]: (previous[job.id] ?? []).map((candidate) =>
              variantIds.includes(candidate.id)
                ? { ...candidate, reviewState: 'attached' as const }
                : candidate,
            ),
          }));
          return;
        }
        for (const variant of pending) {
          await services.callable.reviewAssetVariant({
            creationId: creation.id,
            variantId: variant.id,
            reviewState: 'skipped',
            ...(attributeTo ? { attributeTo } : {}),
          });
          completed += 1;
          setVariantsByJob((previous) => ({
            ...previous,
            [job.id]: (previous[job.id] ?? []).map((candidate) =>
              candidate.id === variant.id
                ? { ...candidate, reviewState: 'skipped' as const }
                : candidate,
            ),
          }));
        }
      });
    } catch (err) {
      setError(
        friendlyError(
          err,
          `${decision === 'attach' ? 'Use all' : 'Keep all'} stopped after ${completed} of ${pending.length} states.`,
        ),
      );
    } finally {
      setAttachingId(null);
    }
  }

  async function skipVariant(variant: AssetVariant) {
    setError('');
    setAttachingId(variant.id);
    try {
      await runAttributed(creation, async (attributeTo) => {
        await services.callable.reviewAssetVariant({
          creationId: creation.id,
          variantId: variant.id,
          reviewState: 'skipped',
          ...(attributeTo ? { attributeTo } : {}),
        });
        setVariantsByJob((previous) =>
          Object.fromEntries(
            Object.entries(previous).map(([jobId, variants]) => [
              jobId,
              variants.map((candidate) =>
                candidate.id === variant.id ? { ...candidate, reviewState: 'skipped' as const } : candidate,
              ),
            ]),
          ),
        );
      });
    } catch (err) {
      setError(friendlyError(err, 'Could not skip this result.'));
    } finally {
      setAttachingId(null);
    }
  }

  async function detach(asset: CreationAsset) {
    const stillSelected = () => currentCreation.current.id === creation.id &&
      currentCreation.current.assets?.some(candidate => candidate.name === asset.name &&
        candidate.variantId === asset.variantId && candidate.url === asset.url &&
        candidate.contentSha256 === asset.contentSha256);
    if (detachInFlight.current || !stillSelected()) return;
    detachInFlight.current = true;
    setError('');
    setDetachingName(asset.name);
    try {
      await runAttributed(creation, async (attributeTo) => {
        // Attribution can wait for a creator choice. Do not act on a different
        // game or a replacement that arrived while that choice was open.
        if (!stillSelected()) throw new Error('The working image changed. Review it again before detaching.');
        await services.callable.detachAsset({
          creationId: creation.id,
          name: asset.name,
          ...(attributeTo ? { attributeTo } : {}),
        });
        // Clear the local attached-marker so the variant's grid button offers
        // a RE-attach (the add-only set otherwise pins it "Attached ✓").
        if (currentCreation.current.id !== creation.id) return;
        setDetachConfirmation(null);
        setAttachedIds((prev) => {
          if (!prev.has(asset.variantId)) return prev;
          const next = new Set(prev);
          next.delete(asset.variantId);
          return next;
        });
      });
    } catch (err) {
      if (currentCreation.current.id === creation.id) setError(friendlyError(err, 'Detach failed.'));
    } finally {
      detachInFlight.current = false;
      setDetachingName(null);
    }
  }

  /** Fill the Generate prompt with a design-derived cover art-direction (the
   *  same composer the auto/backfill covers use). Only offered when the target
   *  kind is `cover`; the creator can tweak the prompt before generating. */
  function coverFromDesign() {
    setName(COVER_NAME);
    setKind('cover');
    setPrompt(
      composeCoverPrompt({
        title: creation.title,
        description: creation.description,
        style: creation.style,
        design: creation.gameDesign,
      }),
    );
    setError('');
  }

  /** Stage picked PNGs as upload rows: default each name to its slugified
   *  filename and kind to `sprite`. Rejects non-PNG / oversize / over-the-count
   *  files with a plain message (the server enforces the same caps). */
  function onFilesPicked(e: React.ChangeEvent<HTMLInputElement>) {
    setError('');
    const picked = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = ''; // allow re-pick of same file
    const rejected: string[] = [];
    const rows: UploadRow[] = [];
    for (const file of picked) {
      if (!isPngUpload(file)) {
        rejected.push(`${file.name} (not a PNG)`);
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        rejected.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB > 2MB)`);
        continue;
      }
      rows.push({
        id: crypto.randomUUID(),
        file,
        objectUrl: URL.createObjectURL(file),
        name: slugify(file.name.replace(/\.png$/i, '')),
        kind: 'sprite',
      });
    }
    setUploads((prev) => {
      const next = [...prev, ...rows];
      if (next.length > MAX_UPLOAD_FILES) {
        rejected.push(`only ${MAX_UPLOAD_FILES} files per upload — extras dropped`);
        for (const r of next.slice(MAX_UPLOAD_FILES)) URL.revokeObjectURL(r.objectUrl);
        return next.slice(0, MAX_UPLOAD_FILES);
      }
      return next;
    });
    if (rejected.length) setError(`Skipped: ${rejected.join(', ')}`);
  }

  function updateRow(id: string, patch: Partial<Pick<UploadRow, 'name' | 'kind'>>) {
    setUploads((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function removeRow(id: string) {
    setUploads((prev) => {
      const row = prev.find((r) => r.id === id);
      if (row) URL.revokeObjectURL(row.objectUrl);
      return prev.filter((r) => r.id !== id);
    });
  }

  function clearUploads() {
    setUploadApproval(null);
    setUploads((prev) => {
      for (const r of prev) URL.revokeObjectURL(r.objectUrl);
      return [];
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  /** Upload every staged PNG + attach each in one call. Confirms if any row
   *  would replace an already-attached asset (same name + kind = edit-in-place). */
  async function uploadAll(approvedSignature?: string) {
    if (uploadInFlight.current || !uploadsValid) return;
    if (uploadReplacements.length && approvedSignature !== uploadSignature) {
      setUploadApproval(uploadSignature);
      return;
    }
    uploadInFlight.current = true;
    setUploadApproval(null);
    setError('');
    setUploading(true);
    try {
      const assets = await Promise.all(
        uploads.map(async (r) => ({
          name: slugify(r.name),
          kind: r.kind,
          dataBase64: await fileToBase64(r.file),
        })),
      );
      if (currentCreationId.current !== creation.id) return;
      await runAttributed(creation, async (attributeTo) => {
        if (currentCreationId.current !== creation.id) return;
        await services.callable.uploadAssets({
          creationId: creation.id,
          assets,
          ...(attributeTo ? { attributeTo } : {}),
        });
        // Upload attaches in one step — done, back to the library.
        if (currentCreationId.current === creation.id) backToLibrary();
      });
    } catch (err) {
      setError(friendlyError(err, 'Upload failed.'));
    } finally {
      uploadInFlight.current = false;
      setUploading(false);
    }
  }

  // Upload-row validity (mirrors the server): every name a valid slug, unique
  // within the batch, and no cross-kind name clash with an attached asset
  // (same name + DIFFERENT kind rejects server-side with `conflict`).
  const uploadCanon = uploads.map((r) => slugify(r.name));
  const uploadRowState = uploads.map((r, i) => {
    const canon = uploadCanon[i];
    const existing = attachedByName.get(canon);
    return {
      canon,
      valid: isValidAssetName(canon),
      dup: uploadCanon.filter((n) => n === canon).length > 1,
      replaces: existing && existing.kind === r.kind ? existing : undefined,
      conflict: existing && existing.kind !== r.kind ? existing : undefined,
    };
  });
  const uploadsValid =
    uploads.length > 0 &&
    uploads.length <= MAX_UPLOAD_FILES &&
    uploadRowState.every((s) => s.valid && !s.dup && !s.conflict);
  const uploadReplacements = uploadRowState.flatMap(s => s.replaces ? [s.replaces] : []);
  const uploadSignature = JSON.stringify({ creationId: creation.id,
    rows: uploads.map(r => [r.id, slugify(r.name), r.kind]),
    replaces: uploadReplacements.map(a => [a.name, a.kind, a.url, a.variantId, a.contentSha256]),
  });
  useEffect(() => {
    if (uploadApproval === uploadSignature) uploadApprovalRef.current?.focus();
  }, [uploadApproval, uploadSignature]);

  const composing = view !== 'library';
  const locked = view === 'edit';

  /** One completed generation's review surface. Static image variants remain
   * alternatives; strip variants are complementary motion states and use the
   * explicit set-review layout approved for the Assets library. */
  const renderVariantGrid = (job: AssetGenJob & { id: string }) => {
    const variants = variantsByJob[job.id] ?? [];
    if (job.kind === 'strip') {
      const reviewedCount = variants.filter(isVariantReviewed).length;
      const pendingCount = variants.length - reviewedCount;
      const bulkBusy = attachingId === `bulk:${job.id}`;
      const allPendingHaveCurrent = variants
        .filter((variant) => !isVariantReviewed(variant))
        .every((variant) => {
          const existing = attachedByName.get((variant.name ?? '').toLowerCase());
          return !!existing && existing.variantId !== variant.id;
        });
      return (
        <section
          className="asset-animation-review"
          key={job.id}
          id={`asset-review-${job.id}`}
          data-testid={`asset-animation-review-${job.id}`}
        >
          <header className="asset-animation-review__header">
            <span className="asset-animation-review__eyebrow">Animation set awaiting review</span>
            <h3 className="panel__subtitle">
              {job.name} · {variants.length} motion state{variants.length === 1 ? '' : 's'}
            </h3>
            <p className="muted">
              These are <strong>separate motions, not alternatives.</strong> Choose whether to use each
              generated animation or keep the current motion where one is already in the game.
            </p>
          </header>
          <div className="asset-animation-review__toolbar">
            <strong data-testid={`asset-animation-progress-${job.id}`}>
              <span>{reviewedCount} of {variants.length}</span> states reviewed
            </strong>
            {pendingCount > 0 && (
              <div className="row asset-animation-review__bulk-actions">
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  onClick={() => void reviewStripSet(job, 'attach')}
                  disabled={attachingId !== null}
                  data-testid={`asset-animation-use-all-${job.id}`}
                >
                  {bulkBusy ? 'Saving…' : `Use all ${pendingCount} new animation${pendingCount === 1 ? '' : 's'}`}
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  onClick={() => void reviewStripSet(job, 'skip')}
                  disabled={attachingId !== null}
                  data-testid={`asset-animation-keep-all-${job.id}`}
                >
                  {allPendingHaveCurrent ? 'Keep all current animations' : 'Skip all generated animations'}
                </button>
              </div>
            )}
          </div>
          <div className="asset-animation-review__states" data-testid={`asset-variants-${job.id}`}>
            {variants.map((variant) => {
              const existing = attachedByName.get((variant.name ?? '').toLowerCase());
              const replaces = existing && existing.variantId !== variant.id ? existing : undefined;
              const attachedState = isVariantAttached(variant);
              const skippedState = variant.reviewState === 'skipped';
              const stateLabel = variant.strip?.state ?? variant.name;
              return (
                <article className="asset-animation-state" key={variant.id}>
                  <div className="asset-animation-state__preview">
                    {variant.strip ? (
                      <StripPreview url={variant.url} strip={variant.strip} />
                    ) : (
                      <img src={variant.url} alt={variant.prompt} />
                    )}
                  </div>
                  <div className="asset-animation-state__identity">
                    <strong>{stateLabel} motion</strong>
                    <span className="muted">{variant.name}</span>
                  </div>
                  <div className="asset-animation-state__decision">
                    <strong>What should the next build use?</strong>
                    <div className="row">
                      <button
                        type="button"
                        className={`btn btn--sm ${attachedState ? 'btn--primary' : ''}`}
                        onClick={() => void attach(variant, replaces)}
                        disabled={attachingId !== null || isVariantReviewed(variant)}
                        data-testid={`asset-attach-${variant.id}`}
                      >
                        {attachedState
                          ? 'New animation selected ✓'
                          : variant.reviewState === 'rejected'
                            ? 'Rejected — blocked'
                          : attachingId === variant.id
                            ? 'Saving…'
                            : 'Use new animation'}
                      </button>
                      <button
                        type="button"
                        className={`btn btn--sm btn--ghost ${skippedState ? 'asset-animation-state__choice--kept' : ''}`}
                        onClick={() => void skipVariant(variant)}
                        disabled={attachingId !== null || isVariantReviewed(variant)}
                        data-testid={`asset-skip-${variant.id}`}
                      >
                        {skippedState
                          ? replaces
                            ? 'Current animation kept ✓'
                            : 'Generated animation skipped ✓'
                          : replaces
                            ? 'Keep current'
                            : 'Don\'t use'}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          <footer className="asset-animation-review__footer">
            <span className="muted">
              {pendingCount > 0
                ? `${pendingCount} decision${pendingCount === 1 ? '' : 's'} remaining. Nothing changes in the live game yet.`
                : 'Review complete. Your choices are ready for the next build.'}
            </span>
          </footer>
        </section>
      );
    }
    const audio = isAudioKind(job.kind);
    return (
      <div key={job.id} style={{ marginTop: 4 }}>
        <h3 className="panel__subtitle" style={{ marginBottom: 6 }}>
          {audio ? 'Listen and decide for' : 'Pick your favorite for'} <strong>{job.name}</strong>{' '}
          <span className="muted">· {KIND_LABEL[job.kind] ?? job.kind}</span>
        </h3>
        {audio && (
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            One candidate per generation. Earlier candidates stay here to choose from; attaching one does not touch
            any published version until you publish saved media.
          </p>
        )}
        <div className="grid" data-testid={`asset-variants-${job.id}`} style={{ paddingBottom: 0 }}>
          {variants.map((v) => {
            // The attached asset this variant would REPLACE on attach — never the
            // variant itself (post-attach the name maps to this variant).
            const existing = attachedByName.get((v.name ?? '').toLowerCase());
            const replaces = existing && existing.variantId !== v.id ? existing : undefined;
            return (
              <div className="card" key={v.id}>
                <div className="card__thumb">
                  {audio
                    ? <audio controls preload="none" src={v.url} aria-label={`Preview candidate ${v.name}`} style={{ maxWidth: '100%' }} />
                    : <img src={v.url} alt={v.prompt} style={{ maxWidth: '100%', maxHeight: '100%' }} />}
                </div>
                <div className="card__body">
                  <div className="card__desc">
                    <strong>{v.strip ? `${v.strip.state} motion` : v.name}</strong>
                    <span className="muted"> · {v.name}</span> · {v.prompt}
                  </div>
                  <button
                    className="btn btn--sm btn--primary"
                    onClick={() => void attach(v, replaces)}
                    // GLOBAL gate: while ANY attach is in flight every attach
                    // button disables — two concurrent attaches race the CAS.
                    disabled={attachingId !== null || isVariantReviewed(v)}
                    data-testid={`asset-attach-${v.id}`}
                  >
                    {isVariantAttached(v)
                      ? 'Attached ✓'
                      : v.reviewState === 'rejected'
                        ? 'Rejected — blocked'
                      : v.reviewState === 'skipped'
                        ? 'Skipped'
                      : attachingId === v.id
                        ? 'Attaching…'
                        : replaces
                          ? `Replace "${replaces.name}"`
                          : 'Approve & attach'}
                  </button>
                  {!isVariantReviewed(v) && (
                    <button
                      className="btn btn--sm btn--ghost"
                      onClick={() => void skipVariant(v)}
                      disabled={attachingId !== null}
                      data-testid={`asset-skip-${v.id}`}
                    >
                      Skip
                    </button>
                  )}
                  {mediaPilot && <button className="btn btn--sm btn--ghost"
                    onClick={() => setReviewTarget({ creationId: creation.id, variantId: v.id })}>Manage reuse</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /** One attached asset card, rendered in exactly one section: either wired to
   * a live game object or available as other attached/support art. */
  const renderAttachedAssetCard = (asset: CreationAsset) => {
    const badge = asset.source ? SOURCE_BADGE[asset.source] : undefined;
    const regenerating = pendingFor(asset.name, asset.kind);
    const liveRoles = liveRolesByAsset.get(asset.name.toLowerCase()) ?? [];
    const refreshSelected = selectedVisualAssetNames.includes(asset.name);
    const redrawable = redrawableAssetNames.has(asset.name.toLowerCase());
    return (
      <div className="card" key={asset.variantId} id={`asset-${asset.name}`}>
        <div className="card__thumb">
          {asset.kind === 'music' || asset.kind === 'sfx' ? (
            <audio controls preload="none" src={asset.url} aria-label={`Preview ${asset.name}`} style={{ maxWidth: '100%' }} />
          ) : asset.kind === 'strip' && asset.strip ? (
            <StripPreview url={asset.url} strip={asset.strip} />
          ) : (
            <img src={asset.url} alt={asset.prompt} style={{ maxWidth: '100%', maxHeight: '100%' }} />
          )}
        </div>
        <div className="card__body">
          <div className="card__desc">
            <strong>{asset.name}</strong> <span className="muted">· {asset.kind}</span>
            {badge && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  padding: '1px 7px',
                  borderRadius: 999,
                  border: '1px solid var(--border)',
                  color: 'var(--text-dim)',
                }}
              >
                {badge}
              </span>
            )}
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            by @{asset.attachedByHandle}
            {regenerating && <span data-testid={`asset-regenerating-${asset.name}`}> · updating…</span>}
          </div>
          <AssetDetails asset={asset} assets={creation.assets ?? []} />
          <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
            <span data-testid={`asset-publication-${asset.name}`}>
              {assetPublicationLabel(assetPublicationState(asset, creation.activeVersion, liveVersion), creation.activeVersion)}
            </span>
          </div>
          <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
            {liveRoles.length > 0
              ? `Role name used by ${creation.activeVersion ? 'live' : 'draft'} build: ${liveRoles.join(', ')}`
              : asset.kind === 'strip'
                ? 'Animation strip'
                : asset.kind === 'cover'
                  ? 'Gallery and title cover'
                  : 'Attached, not in the current playable scene'}
          </div>
          {liveRoles.length > 0 && !redrawable && asset.kind !== 'strip' && (
            <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
              <span data-testid={`asset-not-redrawable-${asset.name}`}>
                The game design does not plan artwork under this name, so a build cannot redraw
                it — use <strong>Edit</strong> to replace the art, or add the role to the game
                design first.
              </span>
            </div>
          )}
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {(mediaPilot || (audioAccess && (asset.kind === 'music' || asset.kind === 'sfx'))) && <button className="btn btn--sm btn--ghost"
              onClick={() => setReviewTarget({ creationId: creation.id, variantId: asset.variantId })}>Manage reuse</button>}
            {asset.kind !== 'strip' && (
              <button
                className="btn btn--sm"
                onClick={() => openEdit(asset)}
                disabled={regenerating}
                data-testid={`asset-edit-${asset.name}`}
                title={isAudioKind(asset.kind) ? 'Generate or upload a replacement under this name' : 'Generate or upload replacement art under this name'}
              >
                {regenerating ? 'Updating…' : 'Edit'}
              </button>
            )}
            {liveRoles.length > 0 && redrawable && (
              <button
                type="button"
                className={`btn btn--sm ${refreshSelected ? 'btn--primary' : 'btn--ghost'}`}
                onClick={() => toggleVisualAsset(asset.name)}
                data-testid={`asset-refresh-${asset.name}`}
                aria-pressed={refreshSelected}
              >
                {refreshSelected ? 'Refresh selected ✓' : 'Refresh in Iterate'}
              </button>
            )}
            <button
              className="btn btn--sm btn--ghost"
              onClick={() => setDetachConfirmation({ creationId: creation.id, asset })}
              disabled={detachingName !== null}
              data-testid={`asset-detach-${asset.name}`}
              title="Detach from the working copy; published versions and reuse are unchanged"
            >
              {detachingName === asset.name ? 'Detaching…' : 'Detach ✕'}
            </button>
          </div>
        </div>
      </div>

    );
  };

  return (
    <div className="panel" data-testid="asset-studio">
      {liveVersion && !isComposedPipeline(liveVersion.pipelineSnapshot) && <p role="status" data-testid="asset-generic-notice">
        {genericNotice}
        {creation.activeVersion > 0 && <> <Link to="?tab=iterate">Rebuild with the new builder</Link></>}
      </p>}
      {decisionPanel}
      {recoverableGeneration && !starting && <section className="panel" aria-label="Recover asset generation">
        <p>A generation request has no saved acknowledgment. Check the library for its job, or restore the same choices and retry safely. Nothing starts until you press Generate.</p>
        <button type="button" className="btn btn--ghost" onClick={restoreGeneration} data-testid="asset-restore-request">Restore unfinished generation request</button>
      </section>}
      {detachConfirmation?.creationId === creation.id && <section
        ref={detachConfirmationRef} tabIndex={-1} aria-label="Confirm working image detach"
        className="panel" data-testid="asset-detach-confirm">
        <h3>Detach “{detachConfirmation.asset.name}” from the working copy?</h3>
        <p>Published versions keep their images. Detaching does not block reuse, and existing game code may still reference this image. You can attach replacement art under the same name.</p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn" disabled={detachingName !== null}
            onClick={() => setDetachConfirmation(null)} data-testid="asset-detach-cancel">Cancel</button>
          <button type="button" className="btn" disabled={detachingName !== null}
            onClick={() => void detach(detachConfirmation.asset)} data-testid="asset-detach-approve">
            {detachingName ? 'Detaching…' : 'Detach working image'}
          </button>
        </div>
      </section>}
      {(mediaPilot || audioAccess) && reviewTarget?.creationId === creation.id && <MediaReviewPanel
        key={`${creation.id}:${reviewTarget.variantId}:${!!reviewTarget.audioOnly}`} creationId={creation.id} variantId={reviewTarget.variantId} audioOnly={reviewTarget.audioOnly}
        onChanged={invalidateMediaChoices} onClose={() => setReviewTarget(null)}
        onSelect={variantId => setReviewTarget({ creationId: creation.id, variantId })} />}
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      >
        <h2 className="panel__title" style={{ marginBottom: 0 }}>
          Assets
        </h2>
        {composing ? (
          <button className="btn btn--sm btn--ghost" onClick={backToLibrary} data-testid="asset-back">
            ← Back to library
          </button>
        ) : (
          <div className="row" style={{ gap: 6 }}>
            <button
              className="btn btn--sm"
              onClick={openCoverComposer}
              data-testid="asset-generate-cover"
              title="Generate a new gallery cover from this game's attached art"
            >
              ✨ Generate cover
            </button>
            {(attached.length > 0 || pendingReviewJobs.length > 0) && (
              <button className="btn btn--primary btn--sm" onClick={() => openNew('sprite')} data-testid="asset-new">
                + New asset
              </button>
            )}
          </div>
        )}
      </div>

      {publishConfirmation?.creationId === creation.id && (
        <section ref={publishConfirmationRef} tabIndex={-1} className="panel" role="group" aria-label="Confirm asset publication" data-testid="assets-publish-confirm" style={{ marginTop: 12 }}>
          <strong>Publish approved media changes?</strong>
          {publishConfirmation.presentationEdit && <p>Apply artwork alignment from v{publishConfirmation.presentationEdit.sourceVersion}: {[...Object.keys(publishConfirmation.presentationEdit.anchors ?? {}), ...Object.keys(publishConfirmation.presentationEdit.visualBounds ?? {}), ...Object.keys(publishConfirmation.presentationEdit.instanceAnchors ?? {}), ...Object.keys(publishConfirmation.presentationEdit.instanceAnchorRemovals ?? {})].join(', ')}. Existing approved media changes will be included too.</p>}
          {pendingBuildChanges.length > 0 && <div data-testid="assets-publish-change-list">
            <p style={{ margin: '8px 0 4px' }}>This version will include:</p>
            <ul style={{ margin: '0 0 8px', paddingLeft: 22 }}>
              {pendingBuildChanges.map((asset) => <li key={asset.variantId}>
                <strong>{asset.name}</strong> — {PUBLISH_KIND_LABEL[asset.kind] ?? asset.kind}
              </li>)}
            </ul>
          </div>}
          {notPublishedChanges.length > 0 && <p data-testid="assets-publish-left-out" style={{ margin: '0 0 8px' }}>
            Not included: {notPublishedChanges.map((asset) => asset.name).join(', ')}.
          </p>}
          <p className="muted" style={{ fontSize: 13, margin: '4px 0 10px' }}>
            Create a new immutable version from this working copy. It reuses current game code and makes no AI gameplay changes; existing live versions stay unchanged.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn btn--primary" onClick={() => void publishApprovedAssets()}
              disabled={publishingAssets || creation.buildState === 'building'} data-testid="assets-publish-confirm-yes">
              {publishingAssets ? 'Publishing…' : 'Confirm publication'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setPublishConfirmation(null)}
              disabled={publishingAssets} data-testid="assets-publish-confirm-no">Cancel</button>
          </div>
        </section>
      )}

      {view === 'library' && creation.activeVersion > 0 && liveVersion?.manifestSummary?.presentation &&
        liveVersion.manifestSummary.presentation.manifestVersion >= 7 && (
        <PresentationPanel key={`${creation.id}:${visualVersion}`} sourceVersion={visualVersion}
          bundleBaseUrl={pinnedPlayUrl(creation.id, visualVersion)}
          shellVersion={liveVersion.shellVersion}
          presentation={liveVersion.manifestSummary.presentation}
          disabled={publishingAssets || creation.buildState === 'building' || isCodeOnlyPipeline(liveVersion.pipelineSnapshot)}
          onPublish={presentationEdit => { setPublishError(''); setPublishConfirmation({ creationId: creation.id, presentationEdit }); }} />
      )}

      <section aria-label="Asset publication stages" className="muted" style={{ marginTop: 12, fontSize: 13 }}>
        <p style={{ margin: 0 }}>Review candidates → attach to working copy → publish a new version.</p>
        <p style={{ margin: '4px 0 0' }}>
          {creation.activeVersion ? `Players currently receive v${creation.activeVersion}. ` : 'This game has no live version yet. '}
          Attaching replacement art does not update gameplay in that version. Older published versions keep their own assets.
        </p>
        <p style={{ margin: '4px 0 0' }}>Skip means “not this choice now.” Detach is not a permanent rejection or a ban on reuse.</p>
        {liveOnlyAssets.length > 0 && (
          <details data-testid="assets-live-only" style={{ marginTop: 6 }}>
            <summary>{liveOnlyAssets.length} live-version asset variant{liveOnlyAssets.length === 1 ? '' : 's'} differ from the working copy</summary>
            <p>Still included in v{creation.activeVersion}: {liveOnlyAssets.map(asset => asset.name).join(', ')}.
              {' '}Replacing or detaching working-copy art does not remove these historical variants.</p>
          </details>
        )}
      </section>

      {/* Running work is immediately useful. Terminal attempts move to the
          collapsed activity log below, where raw worker outcomes do not crowd
          out the creator's asset choices. */}
      {outstanding.length > 0 && (
        <div data-testid="asset-gen-progress" style={{ marginTop: 12 }}>
          {outstanding.map((job) => {
            // Guard the timestamp — a realtime UPDATE replaces the whole row,
            // and a partial/legacy row without createdAt must not render NaN.
            const elapsed =
              typeof job.createdAt === 'number' && Number.isFinite(job.createdAt)
                ? Math.max(0, Math.round((Date.now() - job.createdAt) / 1000))
                : null;
            const stageLabel =
              job.status === 'awaiting-core' ? 'Waiting for core verification…'
                : job.status === 'queued' ? 'Queued…' : STAGE_LABEL[job.stage ?? ''] ?? 'Working…';
            return (
              <div
                key={job.id}
                className="card"
                data-testid={`asset-gen-job-${job.id}`}
                data-status={job.status}
                data-stage={job.stage ?? ''}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, marginBottom: 8 }}
              >
                <span className="spinner" aria-hidden />
                <div>
                  <div>
                    <strong>{job.name}</strong> <span className="muted">· {job.kind}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {stageLabel}
                    {elapsed !== null ? ` · ${elapsed}s` : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {liveVersion?.assetGen?.closure && (
        <div
          className="card"
          data-testid="asset-completion-status"
          style={{ marginTop: 12, padding: 12 }}
        >
          <strong>Playable art status</strong>
          <p className="muted" style={{ fontSize: 12, margin: '5px 0 0' }}>
            {liveVersion.assetGen.defaulted?.length
              ? `Curated artwork is active for ${liveVersion.assetGen.defaulted.join(', ')} while tailored replacements are generated or retried.`
              : `All ${liveVersion.assetGen.closure.materialized.length} published visual asset${liveVersion.assetGen.closure.materialized.length === 1 ? '' : 's'} are packaged with this game version.`}
          </p>
          {liveVersion.assetGen.closure.inferred.length > 0 && (
            <p className="muted" style={{ fontSize: 12, margin: '5px 0 0' }}>
              Newly discovered visual dependencies: {liveVersion.assetGen.closure.inferred.join(', ')}.
              {liveVersion.assetGen.closure.designReconciliationPending
                ? ' Design reconciliation is pending and will be retried.'
                : ''}
            </p>
          )}
          {liveVersion.assetGen.closure.droppedStrips.length > 0 && (
            <p className="error-text" style={{ fontSize: 12, margin: '5px 0 0' }}>
              Animation files failed validation and are holding on static art: {liveVersion.assetGen.closure.droppedStrips.join(', ')}.
            </p>
          )}
        </div>
      )}
      {view === 'library' ? (
        /* ── LIBRARY (home): ready-to-review variants + the attached-asset shelf. ── */
        <>
          <AssetLibraryNavigation active={libraryFilter} onChange={setLibraryFilter} />
          {libraryFilter === 'music' ? (
            <section className="panel" style={{ marginTop: 12 }} data-testid="asset-library-music-empty">
              <h3 className="panel__subtitle">Music</h3>
              {audioAccess ? <>
                <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                  Make a track in the composer — generate it from a brief, choose no music, or upload a WAV — then bind it below and publish saved media.
                </p>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => openNew('music')} data-testid="asset-new-music">+ New music</button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={() => setReviewTarget({ creationId: creation.id, variantId: '', audioOnly: true })}>Rejected audio · review or undo</button>
                </div>
                {visiblePendingReviewJobs.length > 0 && (
                  <div className="asset-review-shelf">{visiblePendingReviewJobs.map(renderVariantGrid)}</div>
                )}
                <div className="grid">{(creation.assets ?? []).filter(a => a.kind === 'music').map(renderAttachedAssetCard)}</div>
                <SfxBindingsPanel key={`${creation.id}:music`} creationId={creation.id} assets={creation.assets} section="music"
                  onPublish={requestAssetPublication} parentHandlesPublishConfirmation publishing={publishingAssets || creation.buildState === 'building' || !isComposedPipeline(liveVersion?.pipelineSnapshot)} />
              </> : <p className="muted">Music upload and binding require membership in this game. Audio generation is not enabled.</p>}
              {readyBuildChangeCount > 0 && <button type="button" className="btn btn--sm btn--primary"
                onClick={requestAssetPublication} disabled={publishingAssets || creation.buildState === 'building' || !isComposedPipeline(liveVersion?.pipelineSnapshot)}>
                Publish approved assets
              </button>}
              {publishError && <p role="alert">{publishError}</p>}
            </section>
          ) : libraryFilter === 'sound-effects' ? (
            <section style={{ marginTop: 12 }} data-testid="asset-library-sound-effects">
              {audioAccess ? <>
                <h3 className="panel__subtitle">Sound effects</h3>
                <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                  Make a sound in the composer — generate it from a description, bind the curated bank, or upload a WAV — then bind it to a cue below and publish saved media.
                </p>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => openNew('sfx')} data-testid="asset-new-sfx">+ New sound effect</button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={() => setReviewTarget({ creationId: creation.id, variantId: '', audioOnly: true })}>Rejected audio · review or undo</button>
                </div>
                {visiblePendingReviewJobs.length > 0 && (
                  <div className="asset-review-shelf">{visiblePendingReviewJobs.map(renderVariantGrid)}</div>
                )}
                <div className="grid">{(creation.assets ?? []).filter(a => a.kind === 'sfx').map(renderAttachedAssetCard)}</div>
                <SfxBindingsPanel key={creation.id} creationId={creation.id} assets={creation.assets} section="sfx"
                  onPublish={requestAssetPublication} parentHandlesPublishConfirmation publishing={publishingAssets || creation.buildState === 'building' || !isComposedPipeline(liveVersion?.pipelineSnapshot)} />
                {publishError && <p role="alert">{publishError}</p>}
              </> : <>
                <h3 className="panel__subtitle">Sound effects</h3>
                <p className="muted">Sound-effect upload and binding require membership in this game. Audio generation is not enabled.</p>
              </>}
              {readyBuildChangeCount > 0 && !audioAccess && <button type="button" className="btn btn--sm btn--primary"
                onClick={requestAssetPublication} disabled={publishingAssets || creation.buildState === 'building' || !isComposedPipeline(liveVersion?.pipelineSnapshot)}>
                Publish approved assets
              </button>}
              {publishError && !audioAccess && <p role="alert">{publishError}</p>}
            </section>
          ) : <>
          {mediaPilot && <button className="btn btn--sm btn--ghost" style={{ marginTop: 12 }}
            onClick={() => setReviewTarget({ creationId: creation.id, variantId: '' })}>Rejected visual assets · pilot</button>}
          {publishPreviewKey && publishPreviewError && (
            <p className="error-text" data-testid="assets-publish-preview-error" style={{ marginTop: 12 }}>{publishPreviewError}</p>
          )}
          {notPublishedChanges.length > 0 && (
            <div className="panel" style={{ marginTop: 12 }} data-testid="assets-not-published">
              <strong style={{ fontSize: 13 }}>
                {notPublishedChanges.length} approved asset{notPublishedChanges.length === 1 ? '' : 's'} won’t be published
              </strong>
              <p data-testid="assets-not-published-names" style={{ fontSize: 12, margin: '4px 0 0' }}>
                {notPublishedChanges.map((asset) => asset.name).join(', ')}
              </p>
              <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                The live game has no media slot for {notPublishedChanges.length === 1 ? 'this asset' : 'these assets'}, so publishing leaves {notPublishedChanges.length === 1 ? 'it' : 'them'} out and the game doesn’t use {notPublishedChanges.length === 1 ? 'it' : 'them'}.
              </p>
            </div>
          )}
          {readyBuildChangeCount > 0 && (
            <div className="panel assets-ready-build" style={{ marginTop: 12 }} data-testid="assets-ready-build">
              <img
                className="assets-ready-build__mascot"
                src="/mascot/release-asset-express.webp"
                alt="Deej sending approved game art through an express checkpoint into a live arcade cabinet"
              />
              <div className="assets-ready-build__copy">
                <span className="assets-ready-build__eyebrow">Fast asset publish</span>
                <strong style={{ fontSize: 13 }}>
                  {readyBuildChangeCount} approved artwork change{readyBuildChangeCount === 1 ? ' is' : 's are'} ready
                </strong>
                <p data-testid="assets-ready-build-names" style={{ fontSize: 12, margin: '4px 0 0' }}>
                  {pendingBuildChanges.map((asset) => asset.name).join(', ')}
                </p>
                <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                  {!isComposedPipeline(liveVersion?.pipelineSnapshot)
                    ? 'This game uses coded visuals. Open Iterate to add artwork or choose an artwork pipeline before publishing assets.'
                    : pendingAnimationReviewJobs.length > 0
                    ? 'Finish reviewing the animation choices, then publish all approved art together.'
                    : 'Reuse the current live game code and publish these assets without an AI iteration or gameplay changes.'}
                </p>
                {publishError && <p className="error-text" data-testid="asset-publish-error">{publishError}</p>}
              </div>
              {pendingAnimationReviewJobs.length > 0 ? (
                <a
                  className="btn btn--sm btn--ghost"
                  href={`#asset-review-${pendingAnimationReviewJobs[0].id}`}
                  data-testid="assets-review-animations"
                >
                  Review animations first
                </a>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={requestAssetPublication}
                  disabled={publishingAssets || creation.buildState === 'building' || !isComposedPipeline(liveVersion?.pipelineSnapshot)}
                  data-testid="assets-publish-fast"
                >
                  {creation.buildState === 'building'
                    ? 'Build in progress…'
                    : publishingAssets
                      ? 'Queueing publish…'
                      : `Publish ${readyBuildChangeCount} approved media change${readyBuildChangeCount === 1 ? '' : 's'}`}
                </button>
              )}
            </div>
          )}
          {visiblePendingReviewJobs.length > 0 && (
            <div className="asset-review-shelf">
              {visiblePendingReviewJobs.some((job) => job.kind !== 'strip') && (
                <h3 className="panel__subtitle" style={{ marginBottom: 6 }}>
                  Choices awaiting review
                </h3>
              )}
              {visiblePendingReviewJobs.map(renderVariantGrid)}
            </div>
          )}
          {visibleAttachedAssets.length === 0 ? (
            visiblePendingReviewJobs.length === 0 && (
              <div style={{ textAlign: 'center', paddingTop: 8 }}>
                <Mascot context="asset-studio-empty" />
                <p className="muted" style={{ fontSize: 13, maxWidth: 440, margin: '4px auto 12px' }}>
                  {libraryFilter === 'all'
                    ? <>No assets yet. Generate art from a prompt or upload your own PNGs — an asset&apos;s<strong> name</strong> is how builds reference it, so name it after the entity it depicts to reskin that entity.</>
                    : libraryFilter === 'visuals'
                      ? 'No visual assets match this filter. Animation strips and sound bindings are separate library views.'
                      : 'No animation strips match this filter. Generate a declared motion state, or return to All to inspect other assets.'}
                </p>
                <button className="btn btn--primary" onClick={() => openNew('sprite')} data-testid="asset-new-empty">
                  + New asset
                </button>
              </div>
            )
          ) : (
            <>
            {libraryFilter !== 'animation' && <>
            <section className="panel" style={{ marginTop: 12 }} data-testid="asset-wiring">
              <strong style={{ fontSize: 13 }}>Game-object wiring</strong>
              <p className="muted" style={{ fontSize: 12, margin: '4px 0 10px' }}>
                The playable build connects these game objects to the asset cards below. Other
                attached art is kept separately, so nothing is shown twice.
              </p>
              {liveVisuals.length > 0 ? (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {liveVisuals.map((role) => (
                    <a
                      key={role.key}
                      href={role.assetName ? `#asset-${role.assetName}` : undefined}
                      className="vocab__chip"
                      style={{ textDecoration: 'none' }}
                    >
                      <strong>{role.label}</strong>
                      <span className="muted">
                        {' → '}
                        {role.assetName ??
                          (role.renderMode === 'background-baked'
                            ? 'active background'
                            : role.renderMode === 'ui'
                              ? 'game UI'
                              : 'game renderer')}
                        {role.animations.length > 0 &&
                          ` · ${role.animations.map((animation) => animation.state).join(' / ')}`}
                        {(role.missingAnimations?.length ?? 0) > 0 && (
                          <span role="status" style={{ display: 'block' }}>
                            Animation art not bound in this version: {role.missingAnimations!.map(animation =>
                              `${animation.state} (${animation.assetName})`).join(', ')}.
                            {' '}These states cannot play their animation strips yet. Check generation
                            progress and attach/rebuild with the matching strips.
                          </span>
                        )}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <span className="muted" style={{ fontSize: 12 }}>
                  Build this game once to show its live object-to-asset wiring.
                </span>
              )}
            </section>
            {selectedVisualAssetNames.length > 0 && (
              <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <Link className="btn btn--primary btn--sm" to={visualRefreshHref} data-testid="visual-refresh-continue">
                  Continue to Iterate · {selectedVisualAssetNames.length} selected
                </Link>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  onClick={() => setSelectedVisualAssets([])}
                >
                  Clear selection
                </button>
              </div>
            )}
            <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
              Use <strong>Edit</strong> to replace one asset in place. For connected art, choose
              <strong> Refresh in Iterate</strong> to redraw several objects as one reviewed change.
            </p>
            </>}
            <div data-testid="attached-assets">
            <section style={{ marginTop: 14 }} data-testid="connected-assets">
              <h3 className="panel__subtitle" style={{ marginBottom: 4 }}>
                Connected in the playable game
              </h3>
              <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
                These assets are currently wired to a visible game object or background.
              </p>
              <div className="grid" style={{ paddingBottom: 0 }}>
                {visibleConnectedAssets.map(renderAttachedAssetCard)}
              </div>
            </section>
            {visibleOtherAssets.length > 0 && (
              <section style={{ marginTop: 16 }} data-testid="other-assets">
                <h3 className="panel__subtitle" style={{ marginBottom: 4 }}>
                  Available for a future build
                </h3>
                <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
                  This art is safely attached and reusable, but the active build does not currently ship it.
                </p>
                <div className="grid" style={{ paddingBottom: 0 }}>
                  {visibleOtherAssets.map(renderAttachedAssetCard)}
                </div>
              </section>
            )}
            </div>
          </>
          )}
          {/* ── Animations: declared states per entity, chipped attached /
              in-flight / missing (missing offers a pre-filled Generate). ── */}
          {(libraryFilter === 'all' || libraryFilter === 'animation') && declaredAnimations.length > 0 && (
            <div style={{ marginTop: 16 }} data-testid="strip-states">
              <h3 className="panel__subtitle" style={{ marginBottom: 6 }}>
                Animations
              </h3>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                Declared motion states per entity. Attached strips apply on the next build.
              </p>
              {declaredAnimations.map(({ entityName, states }) => (
                <div
                  key={entityName}
                  className="row"
                  style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}
                >
                  <strong style={{ fontSize: 13 }}>{entityName}</strong>
                  {states.map((state) => {
                    const status = stripStatus(entityName, state);
                    const slug = slugify(entityName);
                    return (
                      <span
                        key={state}
                        className="vocab__chip"
                        data-testid={`strip-chip-${slug}-${state}`}
                        data-status={status}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      >
                        {status === 'attached' && (
                          <span className="status-dot status-dot--live" aria-label="attached" />
                        )}
                        {state}
                        {status === 'in-flight' && <span className="muted">· working…</span>}
                        {status === 'missing' && (
                          <button
                            className="btn btn--sm btn--ghost"
                            style={{ padding: '0 6px' }}
                            onClick={() => openStripComposer(entityName, state)}
                            data-testid={`strip-generate-${slug}-${state}`}
                            title={`Generate the ${state} strip for ${entityName}`}
                          >
                            Generate
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
          {jobs.some((job) => job.status === 'failed') && (
            <details style={{ marginTop: 20 }} data-testid="asset-activity-log">
              <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
                Generation activity and technical logs ({failedJobs.length} issue
                {failedJobs.length === 1 ? '' : 's'})
              </summary>
              <p className="muted" style={{ fontSize: 12, margin: '8px 0' }}>
                These attempts did not change the game&apos;s current art. You only need this
                history when diagnosing a repeated generation problem.
              </p>
              {failedJobs.map((job) => (
                <div key={job.id} data-testid={`asset-gen-failed-${job.id}`} style={{ marginTop: 8 }}>
                  <strong>{job.name}</strong>{' '}
                  <span className="muted">
                    couldn&apos;t produce a usable {job.kind === 'strip' ? 'animation' : 'asset'}.
                  </span>
                  <JobProcessExpander creationId={creation.id} jobId={job.id} />
                </div>
              ))}
            </details>
          )}
          {libraryFilter === 'all' && audioAccess && <section style={{ marginTop: 16 }} data-testid="asset-library-all-sound-effects">
            <SfxBindingsPanel key={creation.id} creationId={creation.id} assets={creation.assets}
              onPublish={requestAssetPublication} parentHandlesPublishConfirmation publishing={publishingAssets || creation.buildState === 'building' || !isComposedPipeline(liveVersion?.pipelineSnapshot)} />
            {publishError && <p role="alert">{publishError}</p>}
          </section>}
          </>}
        </>
      ) : (
        /* ── COMPOSER wizard (new | edit). ── */
        <>
          {/* Stepper — completed/earlier steps click back; forward is gated by
              the step's own action (Next / Generate delivering variants). */}
          <div className="tabs" data-testid="asset-stepper" style={{ margin: '14px 0', width: 'fit-content' }}>
            {([
              [1, 'What'],
              [2, 'Source'],
              [3, 'Choose'],
            ] as const).map(([n, label]) => (
              <button
                key={n}
                className={`tab${step === n ? ' tab--active' : ''}`}
                onClick={() => n < step && setStep(n)}
                disabled={n > step}
              >
                {n}·{label}
              </button>
            ))}
          </div>

          {/* STEP 1 · What — kind + name (new) or the locked target header (edit). */}
          {step === 1 && (
            <div>
              {locked ? (
                <p style={{ fontSize: 15, marginTop: 0 }}>
                  Editing the <strong>{name}</strong> <span className="muted">{kind}</span> — the
                  next steps make new art and attach it in place of the current image.
                </p>
              ) : (
                <>
                  <div className="row" style={{ marginBottom: 8 }}>
                    <select
                      className="select"
                      style={{ width: 'auto' }}
                      value={kind}
                      onChange={(e) => chooseKind(e.target.value as ComposerKind)}
                      aria-label="Asset kind"
                    >
                      {/* Audio requires creation membership (the server's rule
                          for uploads and generation alike), so a non-member
                          superadmin is not offered it. */}
                      {COMPOSER_KINDS.filter((k) => audioAccess || !isAudioKind(k)).map((k) => (
                        <option key={k} value={k}>
                          {KIND_LABEL[k] ?? k}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p
                    className="muted"
                    style={{ fontSize: 12, marginTop: 0 }}
                    data-testid="asset-kind-help"
                  >
                    <strong style={{ textTransform: 'capitalize' }}>{KIND_LABEL[kind] ?? kind}</strong>{' '}
                    — {KIND_HELP[kind]}{' '}
                    {isAudioKind(kind)
                      ? <>A generation returns <strong>one candidate</strong> to listen to; generating again is your call.</>
                      : <>A generation returns a few <strong>variants</strong> (different takes on your prompt) to pick from.</>}
                  </p>
                  {kind === 'strip' ? (
                    /* Strip target: entity + state pickers — the name is DERIVED. */
                    designEntities.length === 0 ? (
                      <p className="muted" style={{ fontSize: 13 }} data-testid="strip-no-design">
                        Animation strips animate this game&apos;s design entities — there are none
                        yet. Build the game first.
                      </p>
                    ) : (
                      <>
                        <div className="field">
                          <label className="field__label" htmlFor="strip-entity">
                            Entity
                          </label>
                          <select
                            id="strip-entity"
                            className="select"
                            value={stripEntityLive ? stripEntity : ''}
                            onChange={(e) => setStripEntity(e.target.value)}
                            data-testid="strip-entity"
                          >
                            <option value="" disabled>
                              Pick an entity…
                            </option>
                            {designEntities.map((e) => (
                              <option key={e.name} value={e.name} disabled={!hasBaseSprite(e.name)}>
                                {e.name}
                                {hasBaseSprite(e.name) ? '' : ' — needs a base sprite'}
                              </option>
                            ))}
                          </select>
                          <div className="muted" style={{ fontSize: 12 }}>
                            The strip is drawn from the entity&apos;s attached sprite — entities
                            without one need a base sprite generated (and attached) first.
                          </div>
                        </div>
                        <div className="field">
                          <label className="field__label" htmlFor="strip-state">
                            Motion
                          </label>
                          <select
                            id="strip-state"
                            className="select"
                            value={stripState}
                            onChange={(e) => setStripState(e.target.value as AnimStateName)}
                            data-testid="strip-state"
                          >
                            {ANIM_STATE_NAMES.map((s) => (
                              <option key={s} value={s}>
                                {s} · {ANIM_FRAME_DEFAULTS[s]}{' '}
                                {ANIM_FRAME_DEFAULTS[s] === 1 ? 'frame' : 'frames'}
                              </option>
                            ))}
                          </select>
                        </div>
                        {stripName && (
                          <p className="muted" style={{ fontSize: 12 }} data-testid="strip-derived-name">
                            Asset name (derived): <strong>{stripName}</strong> — attaching it
                            applies on the next build.
                          </p>
                        )}
                        {stripEntity && !stripEntityLive && (
                          <p className="error-text" data-testid="strip-entity-vanished">
                            This entity changed in the game design — re-pick one.
                          </p>
                        )}
                      </>
                    )
                  ) : (
                    <div className="field">
                      <label className="field__label" htmlFor="asset-name">
                        Name
                      </label>
                      <input
                        id="asset-name"
                        className="input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="tank, arena-background…"
                        data-testid="asset-name"
                      />
                      <div className="muted" style={{ fontSize: 12 }}>
                        {isAudioKind(kind)
                          ? <>The name you will bind to a cue ("{canonicalName || (kind === 'music' ? 'calm-theme' : 'goal-cheer')}").</>
                          : <>How build prompts reference this asset ("use the {canonicalName || 'tank'} sprite").</>}
                        {name && !nameValid && ' Needs 2–40 chars of lowercase letters, digits, hyphens.'}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="row" style={{ marginTop: 12 }}>
                <button
                  className="btn btn--primary"
                  onClick={() => setStep(2)}
                  disabled={!targetValid}
                  data-testid="asset-step-next"
                >
                  Next: choose a source →
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 · Source — Generate / Curated / Upload, from the kind's table. */}
          {step === 2 && (
            <div>
              {KIND_SOURCES[kind].length > 1 && (
                <div className="tabs" style={{ marginBottom: 12, width: 'fit-content' }} data-testid="asset-source-tabs">
                  {KIND_SOURCES[kind].map((source) => (
                    <button
                      key={source}
                      className={`tab${sourceTab === source ? ' tab--active' : ''}`}
                      onClick={() => setSourceTab(source)}
                      aria-pressed={sourceTab === source}
                      data-testid={`asset-source-${source}`}
                    >
                      {SOURCE_LABEL[source]}
                    </button>
                  ))}
                </div>
              )}

              {audioKind && sourceTab === 'generate' ? (
                /* Audio Generate: the closed brief + the saved avoid line + the
                   training-use disclosure, all beside the ONE action. */
                <div data-testid="audio-generate">
                  <AudioBriefFields
                    value={audioBrief}
                    onChange={setAudioBrief}
                    disabled={starting || !!outstandingForForm || !!audioBlocker}
                    wizardPrefilled={briefFormIsWizardPrefill(audioBrief, creation.gameDesign?.audio)}
                  />
                  <AvoidDirectionField creation={creation} medium="audio" />
                  <p className="muted" style={{ fontSize: 12 }} data-testid="audio-generation-disclosure">
                    {AUDIO_GENERATION_DISCLOSURE[audioKind]}
                  </p>
                  {audioRefusal && (
                    <p className="field__warning" role="alert" data-testid="audio-generate-refused">
                      The provider declined this brief. Its reason: <em>{audioRefusal}</em>. Change the brief and
                      generate again; if the reason is not about your brief, it is a platform problem to report.
                    </p>
                  )}
                  {audioBlocker && (
                    <p className="muted" role="status" data-testid="audio-generate-blocked">{audioBlocker}</p>
                  )}
                  {!audioBlocker && audioBriefProblem && !outstandingForForm && (
                    <p className="muted" style={{ fontSize: 12 }} data-testid="audio-brief-problem">{audioBriefProblem}</p>
                  )}
                  <div className="row">
                    <button
                      className="btn btn--primary"
                      onClick={() => void generate()}
                      disabled={starting || !!outstandingForForm || !!audioBlocker || !!audioBriefProblem || !nameValid}
                      data-testid="asset-generate"
                    >
                      {outstandingForForm
                        ? 'Generating…'
                        : starting
                          ? 'Starting…'
                          : `Generate ${AUDIO_KIND_LABEL[audioKind]} (one candidate)`}
                    </button>
                  </div>
                  {outstandingForForm && (
                    <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                      A generation for <strong>{canonicalName}</strong> is already running — its candidate
                      appears on the next step when it finishes. You can leave this page and come back.
                    </p>
                  )}
                  {outstandingForForm && <Mascot context="asset-studio-generating" size={430} />}
                </div>
              ) : audioKind && sourceTab === 'curated' ? (
                /* Audio Curated: the platform's bank, bound per cue through the
                   ONE bindings surface — no paid call, no file to attach. */
                <div data-testid="audio-curated">
                  <p className="muted" style={{ fontSize: 13 }}>
                    {audioKind === 'sfx'
                      ? <>Platform-bank sounds are rendered locally from reviewed, digest-pinned parameters and can be auditioned or bound without an audio-provider call. Older games may need one normal code build before Studio can identify each event&apos;s exact platform cue. Silence is always free.</>
                      : <>There is no curated music bank. Choosing <strong>no music</strong> is the curated choice for a track — free, and what a game plays until you bind a generated or uploaded track.</>}
                  </p>
                  <SfxBindingsPanel key={`${creation.id}:composer:${audioKind}`} creationId={creation.id} assets={creation.assets} section={audioKind} />
                </div>
              ) : audioKind && sourceTab === 'upload' ? (
                /* Audio Upload: the one WAV upload form, named on the What step. */
                <AudioUploadPanel creation={creation} kind={audioKind} draftStaging={false} initialName={canonicalName} onUploaded={backToLibrary} />
              ) : kind === 'strip' ? (
                /* Strip generation: no prompt — the platform composes it from
                   the entity's sprite + the picked motion. */
                <>
                  <p className="muted" style={{ fontSize: 13 }}>
                    <strong>{stripName || 'The strip'}</strong> will be drawn from{' '}
                    <strong>{stripEntity || 'the entity'}</strong>&apos;s attached sprite — no
                    prompt needed. Approve a variant on the next step; attaching it applies on
                    the next build.
                  </p>
                  {stripEntity && !stripEntityLive && (
                    <p className="error-text" data-testid="strip-entity-vanished">
                      This entity changed in the game design — re-pick one on step 1.
                    </p>
                  )}
                  <div className="row">
                    <button
                      className="btn btn--primary"
                      onClick={() => void generate()}
                      disabled={starting || !!outstandingForForm || !targetValid}
                      data-testid="asset-generate"
                    >
                      {outstandingForForm
                        ? 'Generating…'
                        : starting
                          ? 'Starting…'
                          : 'Generate animation strip'}
                    </button>
                  </div>
                  {outstandingForForm && (
                    <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                      A generation for <strong>{stripName}</strong> is already running — its
                      variants appear on the next step when it finishes. You can leave this page
                      and come back.
                    </p>
                  )}
                  {outstandingForForm && <Mascot context="asset-studio-generating" size={430} />}
                </>
              ) : sourceTab === 'generate' ? (
                <>
                  {kind === 'cover' && (
                    <fieldset className="field" data-testid="cover-source-picker">
                      <legend className="field__label">Cover style</legend>
                      <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
                        <label className={`btn btn--sm${coverSource === 'game-assets' ? ' btn--primary' : ''}`}>
                          <input
                            type="radio"
                            name="cover-source"
                            value="game-assets"
                            checked={coverSource === 'game-assets'}
                            onChange={() => setCoverSource('game-assets')}
                            data-testid="cover-source-game-assets"
                          />{' '}
                          Game assets
                        </label>
                        <label className={`btn btn--sm${coverSource === 'concept' ? ' btn--primary' : ''}`}>
                          <input
                            type="radio"
                            name="cover-source"
                            value="concept"
                            checked={coverSource === 'concept'}
                            onChange={() => setCoverSource('concept')}
                            data-testid="cover-source-concept"
                          />{' '}
                          Concept art
                        </label>
                      </div>
                      <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
                        {coverSource === 'game-assets'
                          ? 'Default — uses your attached game sprites and backgrounds as visual references.'
                          : 'Uses your written game concept only, without referencing attached game art.'}
                      </p>
                    </fieldset>
                  )}
                  <AvoidDirectionField creation={creation} medium="visual" />
                  <div className="field">
                    <div className="field__headrow">
                      <label className="field__label" htmlFor="asset-prompt">
                        Prompt
                      </label>
                      <div className="field__actions">
                        {creation.suggestedAssetPrompt &&
                          prompt !== creation.suggestedAssetPrompt && (
                            <button
                              type="button"
                              className="btn btn--sm btn--ghost"
                              onClick={() => setPrompt(creation.suggestedAssetPrompt ?? '')}
                              data-testid="asset-suggest"
                            >
                              ✨ Suggested example
                            </button>
                          )}
                        {kind === 'cover' && (
                          <button
                            type="button"
                            className="btn btn--sm btn--ghost"
                            onClick={coverFromDesign}
                            title="Prefill a 16:9 cover / menu-splash prompt derived from this game's design"
                            data-testid="cover-from-design"
                          >
                            ✨ Cover from design
                          </button>
                        )}
                        {prompt && (
                          <button
                            type="button"
                            className="btn btn--sm btn--ghost"
                            onClick={() => setPrompt('')}
                            data-testid="asset-clear"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                    <textarea
                      id="asset-prompt"
                      className="textarea"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="A glowing cyan paddle with a chrome bevel, top-down…"
                    />
                  </div>
                  <div className="row">
                    <button
                      className="btn btn--primary"
                      onClick={() => void generate()}
                      disabled={starting || !!outstandingForForm || prompt.trim().length < 4 || !nameValid}
                      data-testid="asset-generate"
                    >
                      {outstandingForForm
                        ? 'Generating…'
                        : starting
                          ? 'Starting…'
                          : 'Generate variants'}
                    </button>
                  </div>
                  {outstandingForForm && (
                    <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                      A generation for <strong>{canonicalName}</strong> is already running — its
                      variants appear on the next step when it finishes. You can leave this page
                      and come back.
                    </p>
                  )}
                  {outstandingForForm && <Mascot context="asset-studio-generating" size={430} />}
                </>
              ) : (
                /* Bring-your-own art: upload finished PNGs + attach them. */
                <div className="field" data-testid="asset-upload">
                  <label className="field__label" htmlFor="asset-upload-input">
                    Upload your own PNGs
                  </label>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                    Made art elsewhere? Upload up to {MAX_UPLOAD_FILES} transparent PNGs (≤2MB
                    each), name each one, and they attach straight to the game — same as a
                    generated asset.
                  </div>
                  <input
                    ref={fileInputRef}
                    id="asset-upload-input"
                    type="file"
                    accept="image/png"
                    multiple
                    onChange={onFilesPicked}
                    disabled={uploading}
                    data-testid="asset-upload-input"
                  />
                  {uploads.length > 0 && (
                    <>
                      <div
                        className="grid"
                        data-testid="asset-upload-rows"
                        style={{ paddingBottom: 0, marginTop: 10 }}
                      >
                        {uploads.map((row, i) => {
                          const s = uploadRowState[i];
                          return (
                            <div className="card" key={row.id}>
                              <div className="card__thumb">
                                <img
                                  src={row.objectUrl}
                                  alt={row.name}
                                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                                />
                              </div>
                              <div className="card__body">
                                <input
                                  className="input"
                                  value={row.name}
                                  onChange={(e) => updateRow(row.id, { name: e.target.value })}
                                  placeholder="player, arena-background…"
                                  aria-label="Upload asset name"
                                  disabled={uploading}
                                  data-testid={`asset-upload-name-${i}`}
                                />
                                <select
                                  className="select"
                                  value={row.kind}
                                  onChange={(e) =>
                                    updateRow(row.id, {
                                      kind: e.target.value as (typeof ASSET_KINDS)[number],
                                    })
                                  }
                                  aria-label="Upload asset kind"
                                  disabled={uploading}
                                  style={{ marginTop: 6 }}
                                >
                                  {ASSET_KINDS.map((k) => (
                                    <option key={k} value={k}>
                                      {k}
                                    </option>
                                  ))}
                                </select>
                                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                                  {!s.valid && '⚠ Needs 2–40 chars of lowercase letters, digits, hyphens.'}
                                  {s.valid && s.dup && '⚠ Duplicate name in this batch.'}
                                  {s.valid && !s.dup && s.conflict &&
                                    `⚠ "${s.canon}" is already a ${s.conflict.kind} — rename or match its kind.`}
                                  {s.valid && !s.dup && !s.conflict && s.replaces &&
                                    `Replaces the attached "${s.canon}".`}
                                </div>
                                <button
                                  className="btn btn--sm btn--ghost"
                                  onClick={() => removeRow(row.id)}
                                  disabled={uploading}
                                  style={{ marginTop: 6 }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {uploadApproval === uploadSignature && (
                        <section ref={uploadApprovalRef} tabIndex={-1} className="field__warning"
                          aria-label="Confirm image replacement" data-testid="asset-upload-confirm">
                          <h4>Replace these images in the working copy?</h4>
                          <p>{uploadReplacements.map(a => a.name).join(', ')}</p>
                          <p>Existing published versions keep their original images. Publish saved media when you are ready to use these replacements in a new game version.</p>
                          <button type="button" className="btn btn--primary" disabled={uploading}
                            data-testid="asset-upload-confirm-yes" onClick={() => void uploadAll(uploadApproval)}>
                            Replace working images
                          </button>
                          <button type="button" className="btn" disabled={uploading}
                            data-testid="asset-upload-confirm-no" onClick={() => setUploadApproval(null)}>Keep current images</button>
                        </section>
                      )}
                      {uploadApproval !== uploadSignature && <div className="row" style={{ marginTop: 10 }}>
                        <button
                          className="btn btn--primary"
                          onClick={() => void uploadAll()}
                          disabled={uploading || !uploadsValid}
                          data-testid="asset-upload-submit"
                        >
                          {uploading
                            ? 'Uploading…'
                            : `Upload & attach ${uploads.length} asset${uploads.length === 1 ? '' : 's'}`}
                        </button>
                        <button
                          className="btn btn--sm btn--ghost"
                          onClick={clearUploads}
                          disabled={uploading}
                        >
                          Clear
                        </button>
                      </div>}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 3 · Choose — the target's candidate grid(s) → approve/attach. */}
          {step === 3 && (
            <div>
              {targetJobs.length === 0 ? (
                <p className="muted" style={{ fontSize: 13 }}>
                  No candidates yet — generate one on the previous step.
                </p>
              ) : (
                targetJobs.map(renderVariantGrid)
              )}
              {audioKind && (
                /* Regeneration is an explicit request (row 1.6): it goes back
                   to the brief and waits for the button — never a hidden retry. */
                <div className="row" style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost"
                    onClick={() => { setStep(2); setSourceTab('generate'); }}
                    data-testid="audio-generate-another"
                  >
                    Generate another (one more candidate, one more charge)
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {error && <div className="error-text">{error}</div>}

      {attributionModal}
    </div>
  );
}
